/* ============================================================================
 * ATELIER SEARCH  (Stage 2: Algolia results with /collections design parity)
 * ----------------------------------------------------------------------------
 * Theme-only, READ-ONLY Algolia. Same engine (bsd-search-core.enhance) + REST
 * contract as the header palette, so /search and the palette agree. NO index/
 * settings/Query-Rule changes - nothing global, nothing live.
 *
 * Renders the page inside a .atelier-collection root so it reuses
 * assets/atelier-collection.css wholesale (cards .pc, shell .ac-shell, facet
 * rail .fgroup/.fopt/.fsw, sort .ac-adrop, filter chips .afp*, empty .ac-empty).
 * This controller owns ALL behaviour (it does NOT load atelier-collection.js,
 * to avoid the #SortBy / facets.js coupling).
 *
 * FACETS: disjunctive faceting via the multi-query endpoint (each group's
 *   counts computed WITHOUT its own selections) - the MFACETS metafield
 *   groups (Finish/Shape/Material/Width/Type/Toilet Format, app-resolved
 *   metaobject labels under meta.custom.*) + Brand(vendor) + Price
 *   (numeric, sticker semantics on variants_min_price).
 * SORT: Relevance (base index) + Best selling (recently_ordered_count_desc
 *   replica - the only sort replica that exists). Price/date have no replica.
 * CARD: full .pc parity (2026-07-17).
 *   - Wishlist hearts render inline: probed on dev - the Swishlist app DOES
 *     delegate-bind, seed (.st-is-added) and commit add/remove on late-injected
 *     .st-wishlist-button nodes, so markup alone = collection-page behaviour.
 *   - Finish dots CAN'T come from Algolia (records carry no metafields; meta:{}
 *     probed live) - they HYDRATE per card from the wishlist single-card
 *     section endpoint as cards near the viewport (Route A - see the hydration
 *     block below + memory atelier-search-card-parity).
 *   - Availability is NEVER derived from Algolia inventory (always-false for
 *     BSD backorder).
 * ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('.asr-page');
  if (!root) return;

  /* ---- config: Algolia app globals, else known public search-only key ---- */
  function conf() {
    try {
      var c = window.algoliaShopify && window.algoliaShopify.config;
      if (c && c.app_id && c.search_api_key) {
        return { app: c.app_id, key: c.search_api_key, prod: (c.index_prefix || 'shopify_') + 'products' };
      }
    } catch (e) { /* fall through */ }
    return { app: '5TBKK66MTH', key: 'a8ded3c32842ccf66d52497783800f1f', prod: 'shopify_products' };
  }
  var BEST_SUFFIX = '_recently_ordered_count_desc';

  var PER = 24;
  /* shared with the header palette's quick-picks */
  var POPULAR = ['matte black tapware', 'freestanding bath', 'wall hung vanity', 'rimless toilet suite', 'brushed gold shower', 'led mirror'];

  /* Brand normalisation. Algolia has only the raw `vendor` field (not the clean
     custom.brands metaobject the .pc card uses), so vendors arrive with duplicate
     spellings and junk. Built from the live distinct-vendor list:
     - merge duplicate spellings into one canonical brand ("Nero Brand"+"Nero"->"Nero")
     - hide non-brand junk ("Bathroom Sales Direct Staging" = the store's own name).
     Add to BRAND_CANON / BRAND_HIDE as the vendor data is cleaned up in Admin. */
  var BRAND_CANON = {
    'Architectural Designer Products (ADP)': 'ADP', 'ADP': 'ADP',
    'Nero Brand': 'Nero', 'Nero': 'Nero',
    'Meir Brand': 'Meir', 'Meir': 'Meir',
    'Greens Tapware': 'Greens', 'Greens': 'Greens',
    'Hotwire Heating': 'Hotwire',
    'Madu Living': 'Madu',
    'Aquato Brand': 'Aquato',
    'Ikon Tapware': 'Ikon'
  };
  var BRAND_HIDE = { 'Bathroom Sales Direct Staging': 1 };
  function canonBrand(v) {
    if (!v) return '';
    return BRAND_CANON[v] || v.replace(/\s+Brand$/i, '');
  }
  /* canonical name -> every raw vendor spelling that maps to it (for filtering) */
  var CANON_TO_VENDORS = {};
  Object.keys(BRAND_CANON).forEach(function (v) {
    var c = BRAND_CANON[v];
    (CANON_TO_VENDORS[c] = CANON_TO_VENDORS[c] || []).push(v);
  });
  function vendorsFor(canon) { return CANON_TO_VENDORS[canon] || [canon]; }
  /* merge raw vendor facet counts into clean brand rows; drop hidden junk */
  function mergeBrandCounts(vCounts) {
    var out = {};
    Object.keys(vCounts || {}).forEach(function (v) {
      if (BRAND_HIDE[v]) return;
      var c = canonBrand(v);
      out[c] = (out[c] || 0) + vCounts[v];
    });
    return out;
  }

  /* ---- metafield facet groups (Stage C, 2026-08-17) ----
     Paths verified live on the base index AND the best-selling replica after
     the app-side facet activation (Collection pages -> Facets -> chips +
     Sync Metafields). Values are app-RESOLVED metaobject references:
     fields.name / fields.colour hold the display labels - no GID maps.
     RAIL_ORDER mirrors the S&D collection-rail order (read from the live rail
     2026-08-17) with Category leading (search's broadest cut) and Price last;
     reordering the rail = reordering these arrays. */
  /* vsort: how a group's VALUES order - 'count' (popularity, default),
     'num' (numeric scale - widths must read as a ruler, count-order
     interleaved 1700mm/750mm/1500mm on broad queries, Chris 2026-08-17),
     'alpha' (stable small sets). */
  var MFACETS = [
    { key: 'finish',   label: 'Finish',        attr: 'meta.custom.colours_metaobjects.fields.colour', style: 'finish', param: 'fin' },
    { key: 'shape',    label: 'Shape',         attr: 'meta.custom.shape.fields.name',                 style: 'shape',  param: 'shape', vsort: 'alpha' },
    { key: 'material', label: 'Material',      attr: 'meta.custom.material.fields.name',              style: 'opt',    param: 'mat' },
    { key: 'width',    label: 'Width',         attr: 'meta.custom.width_band.fields.name',            style: 'opt',    param: 'wid',   vsort: 'num' },
    { key: 'mtype',    label: 'Type',          attr: 'meta.custom.product_type.fields.name',          style: 'opt',    param: 'mtype' },
    { key: 'toilet',   label: 'Toilet Format', attr: 'meta.custom.toilet_format.fields.name',         style: 'opt',    param: 'tf' }
  ];
  /* Group order = the Atelier COLLECTION rail's order (Finish, Shape,
     Material, Width, Type... Brand, Price). The raw product_type "Category"
     group was REMOVED 2026-08-17 (Chris: unfamiliar + not relevant) - it was
     the ungoverned 143-value field; the clean metaobject Type group is its
     replacement and collections never showed it either. */
  var RAIL_ORDER = ['finish', 'shape', 'material', 'width', 'mtype', 'toilet', 'brand', 'price'];
  var MFACET_BY_KEY = {};
  MFACETS.forEach(function (g) { MFACET_BY_KEY[g.key] = g; });

  /* ---- DOM refs ---- */
  var railEl   = root.querySelector('[data-asr-rail]');
  var gridEl   = root.querySelector('[data-asr-grid]');
  var pillsEl  = root.querySelector('[data-asr-pills]');
  var statusEl = root.querySelector('[data-asr-status]');
  var noteEl   = root.querySelector('[data-asr-note]');
  var countEl  = root.querySelector('[data-asr-count]');
  var termEl   = root.querySelector('[data-asr-term]');
  var forEl    = root.querySelector('[data-asr-for]');
  var moreWrap = root.querySelector('[data-asr-more]');
  var moreBtn  = root.querySelector('[data-asr-more-btn]');
  var lmBar    = root.querySelector('[data-asr-lm-bar]');
  var lmShown  = root.querySelector('[data-asr-lm-shown]');
  var lmTotal  = root.querySelector('[data-asr-lm-total]');
  var sortWrap = root.querySelector('[data-asr-sort]');
  var sortBtn  = root.querySelector('[data-asr-sort-btn]');
  var sortLbl  = root.querySelector('[data-asr-sort-label]');
  var inputEl  = root.querySelector('[data-asr-input]');
  var formEl   = root.querySelector('[data-asr-form]');
  var scrimEl  = root.querySelector('[data-asr-scrim]');
  var railBox  = root.querySelector('.ac-rail');
  var densityWrap = root.querySelector('.ac-density');

  var state = {
    q: '', sort: 'relevance',
    page: 0, nbPages: 0, shown: 0,
    f: { vendor: new Set(), priceMin: null, priceMax: null },
    finishPick: null,
    open: { category: true, brand: true, price: true },
    showAll: { category: false, brand: false },
    /* price slider track bounds {lo,hi,step} - snapped from facets_stats.price;
       only updated from responses with NO active price filter, so the track
       never shrinks to the filtered set (see updatePriceBounds) */
    priceBounds: null,
    /* per-query facet-value UNIVERSE {q, counts, stats}: the query's
       UNFILTERED facet lists, captured from request 0 when nothing is ticked
       (free) or from one hpp=0 side request on a cold filtered load. Groups
       render the union of universe + current counts - a value with no matches
       under the current filters greys out in place (.dis) instead of
       vanishing, and value ORDER freezes to the universe's, so the rail never
       reshuffles while a query is refined (Chris 2026-08-19; mirrors the
       /collections rail = S&D "Empty values: show in default order" + .dis). */
    uni: null,
    hasResults: false,
    seq: 0, abort: null, loading: false
  };
  /* one Set + open/show-all slot per metafield group (all-open default,
     matching the collection rail's shipped decision) */
  MFACETS.forEach(function (g) {
    state.f[g.key] = new Set();
    state.open[g.key] = true;
    state.showAll[g.key] = false;
  });

  /* capped infinite scroll: number of auto-fired loads so far, and the cap before we
     hand back to the manual button. Resets to 0 on any new result set (see run). */
  var autoLoads = 0, AUTO_MAX = 3;

  /* ---- helpers ---- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  function aud(n) { return '$' + Math.round(n).toLocaleString('en-AU'); }
  function setStatus(html) { statusEl.innerHTML = html || ''; statusEl.style.display = html ? '' : 'none'; }
  /* Set -> Array. NOT slice.call(set): a Set has no .length, so that returns []
     (this silently emptied every facetFilter/pill/URL param until 2026-07-15). */
  function arr(set) { return Array.from(set); }
  function anyFilter() {
    if (state.f.vendor.size || state.f.priceMin != null || state.f.priceMax != null) return true;
    return MFACETS.some(function (g) { return state.f[g.key].size > 0; });
  }

  /* intents (engine v1.7): a size or "floor standing" in the query becomes a
     facet filter (ff) on the stripped text, plus `extras` - the original text
     over the products that filter excludes, so nothing that matches today is
     lost. ff rides on EVERY request (hits, facet counts, universe) so counts
     describe the filtered result set; extras are appended after page 1. */
  function enhanceQ(q, skipFinish) {
    if (window.BSDSearch && BSDSearch.enhance) {
      try {
        var e = BSDSearch.enhance(q, { skipFinish: !!skipFinish, intents: true });
        var ff = e.facetFilters || [];
        /* key = the universe-cache identity: "1200 vanity" runs as "vanity" +
           a width filter, so the stripped text alone would collide with a
           plain "vanity" search's cached facet universe */
        return { q: e.query || q, of: e.optionalFilters || [], ff: ff, extras: e.extras || [], size: e.size || null,
          key: (e.query || q) + (ff.length ? ' ' + JSON.stringify(ff) : '') };
      }
      catch (_) {}
    }
    return { q: q, of: [], ff: [], extras: [], size: null, key: q };
  }

  /* the query to actually run: family-expanded by default, or — when a finish
     chip is picked — that single finish (literal) + the non-finish remainder.
     famQuery is always the family-expanded form, so the callout still detects it. */
  function buildQuery() {
    var enhFam = enhanceQ(state.q, false);
    var fam = (window.BSDSearch && BSDSearch.detectFinishFamily) ? BSDSearch.detectFinishFamily(enhFam.q) : null;
    if (state.finishPick && !fam) state.finishPick = null;   // query is no longer a finish search — drop stale pick
    var enh = enhFam;
    if (state.finishPick && fam) {
      var rest = enhFam.q.split(' ').filter(function (t) { return t !== fam; }).join(' ');
      enh = enhanceQ((state.finishPick + ' ' + rest).trim(), true);
    }
    return { enh: enh, famQuery: enhFam.q, fam: fam };
  }

  /* facetFilters as a disjunctive structure, optionally excluding one GROUP
     (by state key) so that group's own counts aren't narrowed by its own
     selections. */
  function ffArr(excludeKey) {
    var out = [];
    if (excludeKey !== 'brand' && state.f.vendor.size) {
      var ors = [];
      arr(state.f.vendor).forEach(function (canon) {
        vendorsFor(canon).forEach(function (v) { ors.push('vendor:' + v); });
      });
      out.push(ors);
    }
    MFACETS.forEach(function (g) {
      if (excludeKey === g.key) return;
      var s = state.f[g.key];
      if (s && s.size) out.push(arr(s).map(function (v) { return g.attr + ':' + v; }));
    });
    return out;
  }
  /* Price filters on variants_min_price - the card's sticker ("From $X") - NOT
     the variant-level `price`. The index holds variant records, so a `price`
     filter matches ANY variant in range: Chris's 2026-08-17 report - a
     $2,500-$2,700 range surfaced "From $2,340" cards whose thermostat-upgrade
     variant was $2,546. What the range means must be what the sticker shows.
     (Numeric attributes filter without any index declaration - probed live.) */
  function numArr() {
    var a = [];
    if (state.f.priceMin != null) a.push('variants_min_price>=' + state.f.priceMin);
    if (state.f.priceMax != null) a.push('variants_min_price<=' + state.f.priceMax);
    return a;
  }

  function buildParams(o) {
    /* queryType=prefixNone: /search queries are COMMITTED (the user pressed
       Enter), so words match whole, not as prefixes. Under the index default
       prefixAll, "bath" swallowed every "Bathroom..." title AND the staging
       vendor string (highlight-probed 2026-08-17: the Sensowash toilet
       matched via vendor "Bathroom Sales Direct Staging"). The palette keeps
       prefix matching - as-you-type is where prefixes belong. Pairs with the
       one-way bath->bathtub index synonym (bsd-syn-bathtub) - without it,
       prefixNone would drop "Bathtub"-titled products from "bath". Applies to
       ALL page requests (hits + facet counts) so counts match the grid. */
    var p = 'query=' + encodeURIComponent(o.query || '') +
      '&hitsPerPage=' + o.hpp + '&page=' + (o.page || 0) +
      '&' + (o.queryType || 'queryType=prefixNone') + '&distinct=true&clickAnalytics=false';
    if (o.facets) p += '&facets=' + encodeURIComponent(JSON.stringify(o.facets));
    /* the index holds VARIANT records - without this, facet counts count
       variants, not products (probed live 2026-08-17: "heating" showed
       Hotwire 289 vs the true 103). Same param the palette rail sends. */
    if (o.afd) p += '&facetingAfterDistinct=true';
    var ff = (o.facetFilters || []).concat(o.intentFF || []);
    if (ff.length) p += '&facetFilters=' + encodeURIComponent(JSON.stringify(ff));
    if (o.filters) p += '&filters=' + encodeURIComponent(o.filters);
    if (o.numeric && o.numeric.length) p += '&numericFilters=' + encodeURIComponent(JSON.stringify(o.numeric));
    if (o.optionalFilters && o.optionalFilters.length) {
      p += '&optionalFilters=' + encodeURIComponent(JSON.stringify(o.optionalFilters)) + '&sumOrFiltersScores=true';
    }
    if (o.maxValuesPerFacet) p += '&maxValuesPerFacet=' + o.maxValuesPerFacet;
    if (o.strict && window.BSDSearch && BSDSearch.EXTRA_PARAMS) p += '&' + BSDSearch.EXTRA_PARAMS;
    return p;
  }

  function sortIndex(A) { return state.sort === 'bestselling' ? A.prod + BEST_SUFFIX : A.prod; }

  /* size search: re-point each multi-size card's links at its variant of the
     searched width (the VB / product page opens at that size). Links only -
     the card shows the "From" sticker, so nothing visible changes and no
     price can jump. Failure leaves the plain product links. Own request, so
     a new search's fetchA abort can't cancel it mid-flight; stale replies
     patch nothing because the cards are gone. */
  function sizeLinks(hits, size) {
    if (!size || !window.BSDSearch || !BSDSearch.sizeVariantRequests) return;
    var vr = BSDSearch.sizeVariantRequests(hits, size);
    if (!vr.length) return;
    var A = conf();
    fetch('https://' + A.app + '-dsn.algolia.net/1/indexes/*/queries', {
      method: 'POST',
      headers: { 'X-Algolia-Application-Id': A.app, 'X-Algolia-API-Key': A.key },
      body: JSON.stringify({ requests: vr.map(function (x) { return { indexName: A.prod, params: x.params }; }) })
    }).then(function (r) { return r.ok ? r.json() : null; }).then(function (d) {
      if (!d) return;
      vr.forEach(function (x, k) {
        var v = BSDSearch.variantForSize((d.results[k] && d.results[k].hits) || [], size);
        if (!v) return;
        var h = hits[x.i], base = '/products/' + encodeURIComponent(h.handle);
        gridEl.querySelectorAll('[data-handle="' + String(h.handle).replace(/"/g, '') + '"] a[href="' + base + '"]').forEach(function (a) {
          a.setAttribute('href', base + '?variant=' + v.objectID);
        });
      });
    }).catch(function () {});
  }

  function fetchA(requests) {
    var A = conf();
    if (state.abort) state.abort.abort();
    state.abort = new AbortController();
    return fetch('https://' + A.app + '-dsn.algolia.net/1/indexes/*/queries', {
      method: 'POST', signal: state.abort.signal,
      headers: { 'X-Algolia-Application-Id': A.app, 'X-Algolia-API-Key': A.key },
      body: JSON.stringify({ requests: requests })
    }).then(function (r) { if (!r.ok) throw new Error('algolia ' + r.status); return r.json(); })
      .then(function (d) { return d.results; });
  }

  /* ---- SVG bits (match the .pc card + facet markup) ---- */
  var SVG_CART  = '<svg viewBox="0 0 24 24"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>';
  var SVG_SHIP  = '<svg viewBox="0 0 24 24"><path d="M3 7h13v10H3z"/><path d="M16 10h4l1 3v4h-5z"/><circle cx="7" cy="18" r="1.6"/><circle cx="18" cy="18" r="1.6"/></svg>';
  var SVG_WARR  = '<svg viewBox="0 0 24 24"><path d="M12 2 9.2 8.6 2 9.2l5.5 4.7L5.8 21 12 17.3 18.2 21l-1.7-7.1L22 9.2l-7.2-.6z"/></svg>';
  var SVG_VB    = '<svg viewBox="0 0 24 24"><path d="M4 7h7M4 12h12M4 17h6"/><circle cx="15" cy="7" r="2"/><circle cx="18" cy="12" r="2"/><circle cx="12" cy="17" r="2"/></svg>';
  var SVG_CHEV  = '<svg viewBox="0 0 24 24"><path d="m6 9 6 6 6-6"/></svg>';
  var SVG_CHECK = '<svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>';
  var SVG_X     = '<svg viewBox="0 0 24 24"><path d="M5 5l14 14M19 5 5 19"/></svg>';
  var SVG_FIND  = '<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="m20 20-3-3"/><path d="M8 11h6"/></svg>';

  /* ---- shared field helpers (grid card + list row) ---- */
  function imgUrl(h) {
    var img = h.product_image || h.image || '';
    if (img.indexOf('//') === 0) img = 'https:' + img;
    if (img) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=600';
    return img;
  }
  function priceBits(h) {
    var min = +h.variants_min_price || +h.price || 0;
    var max = +h.variants_max_price || min;
    var cmp = +h.compare_at_price || +h.variants_compare_at_price_min || 0;
    return { min: min, max: max, cmp: cmp, off: cmp > min ? Math.round((1 - min / cmp) * 100) : 0, from: max > min };
  }
  function shipWarr(h) {
    return {
      ship: Array.isArray(h.collections) && h.collections.indexOf('instant-ship') > -1,
      warr: Array.isArray(h.tags) && h.tags.indexOf('15-year-warranty') > -1,
      /* vanity-builder-product = the search-side mirror of
         custom.vanity_builder (Algolia sees tags only); applied by
         scripts/add_vanity_builder_tag.py, parity guarded by
         verify_brand_finishes.py. NOT the bare `vanity-builder` tag -
         that one sits on 100+ ADP basins as import metadata. */
      vb: Array.isArray(h.tags) && h.tags.indexOf('vanity-builder-product') > -1
    };
  }
  /* Build vanity (VB) / ATC (single variant) / View options (multi) */
  function actHTML(h) {
    var url = '/products/' + encodeURIComponent(h.handle);
    if (Array.isArray(h.tags) && h.tags.indexOf('vanity-builder-product') > -1) {
      return '<a class="pc-atc build" href="' + url + '" aria-label="Build this vanity - ' + esc(h.title) + '">' +
        SVG_VB + '<span class="lbl">Build vanity</span></a>';
    }
    if (+h.variants_count === 1) {
      return '<product-form class="pc-atc-form"><form method="post" action="/cart/add" class="quick-add-form" data-type="add-to-cart-form">' +
        '<input type="hidden" name="id" value="' + esc(h.objectID) + '">' +
        '<button type="submit" class="pc-atc add" data-product-handle="' + esc(h.handle) + '" aria-label="Add ' + esc(h.title) + ' to cart">' +
          '<span class="spin"></span>' + SVG_CART + '<span class="lbl">Add to cart</span>' +
        '</button>' +
        '<span class="loading__spinner hidden" style="display:none !important;"></span>' +
      '</form></product-form>';
    }
    return '<a class="pc-atc view" href="' + url + '" aria-label="View ' + esc(h.title) + '"><span class="lbl">View options</span></a>';
  }
  /* wishlist heart - same markup as the Liquid card. The Swishlist app
     delegate-binds + seeds late-injected buttons (dev-probed 2026-07-17), and
     the toast/flight FX engine (atelier-wishlist-toast.js) delegates on
     document, so this needs NO wiring here. h.id = product id (objectID is the
     variant id). */
  function wishHTML(h) {
    return '<button type="button" class="pc-wish st-wishlist-button" data-handle="' + esc(h.handle) + '" data-product-id="' + esc(h.id) + '" aria-label="Add ' + esc(h.title) + ' to wishlist"></button>';
  }
  /* first-sentence-ish description snippet from body_html_safe (list row only) */
  function snippetFrom(h) {
    var d = String(h.body_html_safe || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (!d) return '';
    var first = d.split('. ')[0];
    if (first.length > 30 && first.length < 170) return esc(first + '.');
    var w = d.split(' ');
    return w.length > 22 ? esc(w.slice(0, 22).join(' ')) + '…' : esc(d);
  }

  /* ---- grid card (.pc - finish dots hydrate in via the section fetch below) ---- */
  function cardHTML(h, i) {
    var img = imgUrl(h), p = priceBits(h), sw = shipWarr(h);
    var brand = BRAND_HIDE[h.vendor] ? '' : canonBrand(h.vendor);
    var url = '/products/' + encodeURIComponent(h.handle);
    var badges = (sw.vb || sw.ship || sw.warr)
      ? '<div class="pc-badges">' +
          (sw.vb ? '<span class="pbadge vb">' + SVG_VB + 'Vanity Builder</span>' : '') +
          (sw.ship ? '<span class="pbadge ship">' + SVG_SHIP + 'Instant Ship</span>' : '') +
          (sw.warr ? '<span class="pbadge warr">' + SVG_WARR + '15 Yr Warranty</span>' : '') +
        '</div>'
      : '';
    /* et-bg = the instant underlay (64px stretched + CSS-blurred); the accurate
       edge-tint strips overtake it (atelier-edge-tint.js). Shared URL + CORS
       mode with the sampler = one download. SYNC: snippets/atelier-product-card
       .liquid + atelier-header.js emit the same element (width=64 everywhere). */
    var bg32 = null;
    if (img) { var b64 = img.replace(/([?&])width=\d+/, '$1width=64'); if (b64 !== img) bg32 = b64; }
    return '<div class="pc asr-enter" style="--i:' + (i || 0) + '" data-product data-handle="' + esc(h.handle) + '">' +
      '<div class="pc-im">' + badges +
        (bg32 ? '<img class="et-bg" src="' + bg32 + '" alt="" aria-hidden="true" loading="lazy" fetchpriority="low" crossorigin="anonymous">' : '') +
        '<a href="' + url + '" class="pc-im-link" aria-label="' + esc(h.title) + '">' +
          (img ? '<img class="main" loading="lazy" src="' + img + '" alt="' + esc(h.title) + '">' : '<span class="pc-im-ph"></span>') +
        '</a>' +
      '</div>' +
      '<div class="pc-bd">' +
        (brand ? '<span class="pc-vd">' + esc(brand) + '</span>' : '') +
        '<a class="pc-tt" href="' + url + '">' + esc(h.title) + '</a>' +
        '<div class="pc-pr">' +
          (p.from ? '<span class="from">From</span>' : '') +
          '<span class="now">' + aud(p.min) + '</span>' +
          (p.off ? '<span class="was">' + aud(p.cmp) + '</span><span class="off">' + p.off + '% OFF</span>' : '') +
        '</div>' +
        '<div class="pc-act">' + actHTML(h) + wishHTML(h) + '</div>' +
      '</div>' +
    '</div>';
  }

  /* ---- list row (.lrow - Brand / Model / You-save + a description snippet;
     the fins row + Finish fact hydrate in via the section fetch below) ---- */
  function rowHTML(h, i) {
    var img = imgUrl(h), p = priceBits(h), sw = shipWarr(h);
    var brand = BRAND_HIDE[h.vendor] ? '' : canonBrand(h.vendor);
    var url = '/products/' + encodeURIComponent(h.handle);
    var snip = snippetFrom(h);
    var tags = (sw.vb || sw.ship || sw.warr)
      ? '<div class="lrow-tags">' +
          (sw.vb ? '<span class="ltag vb">' + SVG_VB + 'Vanity Builder</span>' : '') +
          (sw.ship ? '<span class="ltag ship">' + SVG_SHIP + 'Instant Ship</span>' : '') +
          (sw.warr ? '<span class="ltag warr">' + SVG_WARR + '15 Yr Warranty</span>' : '') +
        '</div>'
      : '';
    var facts = '';
    if (brand) facts += '<span class="lfact"><span class="k">Brand</span><span class="v">' + esc(brand) + '</span></span>';
    if (h.sku) facts += '<span class="lfact"><span class="k">Model</span><span class="v">' + esc(h.sku) + '</span></span>';
    if (p.off) facts += '<span class="lfact"><span class="k">You save</span><span class="v"><b class="sv-amt">' + aud(p.cmp - p.min) + '</b> <span class="sv-pct">' + p.off + '% off</span></span></span>';
    return '<article class="lrow asr-enter" style="--i:' + (i || 0) + '" data-product data-handle="' + esc(h.handle) + '">' +
      '<div class="lrow-im"><a href="' + url + '" class="lrow-im-link" aria-label="' + esc(h.title) + '">' +
        (img ? '<img class="main" loading="lazy" src="' + img + '" alt="' + esc(h.title) + '">' : '<span class="pc-im-ph"></span>') +
      '</a></div>' +
      '<div class="lrow-bd">' +
        '<a class="lrow-tt" href="' + url + '">' + esc(h.title) + '</a>' +
        tags +
        (snip ? '<p class="lrow-snip">' + snip + '</p>' : '') +
        (facts ? '<div class="lrow-facts">' + facts + '</div>' : '') +
      '</div>' +
      '<div class="lrow-pr">' +
        '<div class="lrow-price">' + (p.from ? '<span class="from">From</span>' : '') +
          '<div class="lrow-price-line"><span class="now">' + aud(p.min) + '</span>' +
          (p.off ? '<span class="was">' + aud(p.cmp) + '</span><span class="off">' + p.off + '% OFF</span>' : '') +
          '</div></div>' +
        '<div class="lrow-act">' + actHTML(h) + wishHTML(h) + '</div>' +
      '</div>' +
    '</article>';
  }

  /* both rendered per result; CSS shows grid card OR list row per [data-ac-current-view] */
  var settleT = null;
  function itemHTML(h, i) {
    var ci = i > 14 ? 14 : (i || 0);   // cap the stagger delay so deep cards don't lag
    return cardHTML(h, ci) + rowHTML(h, ci);
  }
  /* strip the entrance class once it's played, so grid/list toggles don't replay it */
  function settleStagger() {
    if (settleT) clearTimeout(settleT);
    settleT = setTimeout(function () {
      Array.prototype.forEach.call(gridEl.querySelectorAll('.asr-enter'), function (el) { el.classList.remove('asr-enter'); });
    }, 950);
  }

  /* ================= card hydration (Route A) =================
     Algolia records carry NO metafields (probed live 2026-07-17: meta is {}),
     so the finish dots (custom.colour_sibling_products), the metaobject brand
     name and the row's Finish fact can't be built from the index. Instead, as
     a card nears the viewport we fetch the product's REAL Liquid card ONCE via
     the existing wishlist single-card endpoint
     (/products/<handle>?section_id=atelier-wishlist-card - the same endpoint
     the wishlist page and the finish-bloom enrichment already use) and
     transplant the parts the JS card can't build:
       - .pc-fins into the grid card (VISIBLE - the hover image-swap and the
         hold-to-preview bloom then work from real dots) and into the list row
       - the metaobject brand name over the vendor-derived eyebrow
         (replace-only: never added or removed, so BRAND_HIDE + layout hold)
       - the row's Finish fact (name from the .aw-cm meta node + dot count)
       - SOLD-OUT state (soldtag + "Currently unavailable" + disabled button),
         from Liquid's product.available - the one reliable signal (Chris
         2026-07-17: search cards must not look buyable when they aren't).
     Cache: per handle, in-memory + sessionStorage (refines and back-restore
     re-apply with NO refetch; fetch failures cache in-memory only so a
     transient error retries on the next page view).
     Route B - indexing the metafields into Algolia via the app config - is the
     eventual fix (also unlocks a finish FACET) but is a global live-index
     schema change; parked. See memory atelier-search-card-parity. */
  var hydMem = {};                 // handle -> bits {fins,brand,fin,nf}(+err)
  var hydPending = {};             // handle -> 1 while queued or in flight
  var hydRetried = {};             // handle -> 1 after its single 429 retry
  var hydQueue = [], hydActive = 0, HYD_MAX = 3, HYD_SPACE = 140;
  var hydTimer = null, hydIO = null;

  function hydCacheGet(h) {
    if (hydMem[h]) return hydMem[h];
    try {
      var raw = sessionStorage.getItem('asrF3:' + h);   /* F3: entries gained the po (preorder) field 2026-08-11 */
      if (raw) return (hydMem[h] = JSON.parse(raw));
    } catch (e) {}
    return null;
  }
  function hydCachePut(h, bits) {
    hydMem[h] = bits;
    if (bits.err) return;   // transient failure: don't persist it
    try { sessionStorage.setItem('asrF3:' + h, JSON.stringify(bits)); } catch (e) {}
  }
  function hydExtract(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fins = doc.querySelector('.pc-fins');
    var vd = doc.querySelector('.pc-vd');
    var cm = doc.querySelector('.aw-cm');
    var pc = doc.querySelector('.pc');
    return {
      fins: fins ? fins.outerHTML : '',
      brand: vd ? vd.textContent.trim() : '',
      fin: cm ? (cm.getAttribute('data-finish-name') || '') : '',
      nf: fins ? fins.querySelectorAll('.pc-fin').length : 0,
      /* Liquid truth for availability (product.available) - Algolia's
         inventory field is unusable (always-false for BSD backorder), so this
         is the ONLY reliable sold-out signal for search cards */
      sold: (pc && pc.classList.contains('is-sold')) ? 1 : 0,
      /* Stoq preorder: the Liquid card carries .preorder + a selling_plan
         input (Algolia has no selling-plan data) - po = the plan id */
      po: (function () {
        var b = doc.querySelector('.pc-atc.preorder');
        var sp = doc.querySelector('input[name="selling_plan"]');
        return (b && sp) ? sp.value : 0;
      })()
    };
  }
  /* Rebuild a card/row into its sold-out state, mirroring what
     atelier-product-card.liquid / atelier-product-row.liquid render when
     product.available == false (soldtag, "Currently unavailable" price,
     disabled Sold-out button in place of the ATC; the wishlist heart stays). */
  function soldify(el, isCard) {
    el.classList.add('is-sold');
    if (isCard) {
      var im = el.querySelector('.pc-im');
      if (im && !im.querySelector('.soldtag')) {
        var b = im.querySelector('.pc-badges');
        if (b) b.parentNode.removeChild(b);
        im.insertAdjacentHTML('afterbegin', '<span class="soldtag">Sold out</span>');
      }
      var pr = el.querySelector('.pc-pr');
      if (pr) pr.innerHTML = '<span class="now" style="color:var(--ac-mute)">Currently unavailable</span>';
      var act = el.querySelector('.pc-act');
      if (act && !act.querySelector('.pc-atc.sold')) {
        var atc = act.querySelector('.pc-atc-form') || act.querySelector('.pc-atc');
        if (atc) atc.parentNode.removeChild(atc);
        act.insertAdjacentHTML('afterbegin', '<span class="pc-atc sold">Sold out</span>');
      }
    } else {
      var lim = el.querySelector('.lrow-im');
      if (lim && !lim.querySelector('.soldtag')) lim.insertAdjacentHTML('afterbegin', '<span class="soldtag">Sold out</span>');
      var pw = el.querySelector('.lrow-price');
      if (pw) pw.innerHTML = '<div class="lrow-price-line"><span class="now" style="font-size:16px;color:var(--ac-mute)">Currently unavailable</span></div>';
      var lact = el.querySelector('.lrow-act');
      if (lact && !lact.querySelector('.pc-atc.sold')) {
        var latc = lact.querySelector('.pc-atc-form') || lact.querySelector('.pc-atc');
        if (latc) latc.parentNode.removeChild(latc);
        lact.insertAdjacentHTML('afterbegin', '<span class="pc-atc sold">Sold out</span>');
      }
    }
  }
  /* Rebuild a card/row ATC into the preorder variant, mirroring what
     atelier-product-card.liquid renders when the variant carries a Stoq
     selling plan: .preorder chip + truck glyph + "Preorder" label + the
     hidden selling_plan input so quick-add books a real preorder line. */
  var SVG_TRUCK = '<svg viewBox="0 0 24 24"><rect x="1" y="3" width="15" height="13" rx="1.5"/><path d="M16 8h4l3 3v5h-7z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>';
  function preorderify(el, isCard, planId) {
    var act = el.querySelector(isCard ? '.pc-act' : '.lrow-act');
    var btn = act && act.querySelector('.pc-atc.add');
    if (!btn || btn.classList.contains('preorder')) return;
    btn.classList.add('preorder');
    var a = btn.getAttribute('aria-label');
    if (a) btn.setAttribute('aria-label', a.replace(/^Add /, 'Preorder ').replace(/ to cart$/, ''));
    var svg = btn.querySelector('svg');
    if (svg) svg.outerHTML = SVG_TRUCK;
    var lbl = btn.querySelector('.lbl');
    if (lbl) lbl.textContent = 'Preorder';
    var form = act.querySelector('form.quick-add-form');
    if (form && !form.querySelector('input[name="selling_plan"]')) {
      var inp = document.createElement('input');
      inp.type = 'hidden';
      inp.name = 'selling_plan';
      inp.value = planId;
      form.appendChild(inp);
    }
  }
  function finFactHTML(bits) {
    return '<span class="lfact"><span class="k">Finish</span><span class="v">' + esc(bits.fin) +
      (bits.nf > 1 ? ' <span class="sv-pct">&middot; ' + bits.nf + ' finishes</span>' : '') + '</span></span>';
  }
  function factByLabel(rowEl, label) {
    var ks = rowEl.querySelectorAll('.lrow-facts .lfact .k');
    for (var i = 0; i < ks.length; i++) if (ks[i].textContent === label) return ks[i].closest('.lfact');
    return null;
  }
  /* Apply bits to this handle's VISIBLE elements now. Hidden view-twins
     (display:none) are deliberately skipped - they keep no data-hyd and fill
     from cache when the view toggles (applyView -> hydrateNewCards). Halves
     the anchors the Restock Rocket app scans+fetches per pass. */
  function hydApplyNow(handle, bits) {
    Array.prototype.forEach.call(gridEl.querySelectorAll('[data-handle="' + handle + '"]'), function (el) {
      if (el.dataset.hyd === '1') return;
      if (el.offsetParent === null) return;   // hidden twin: fills on view toggle
      el.dataset.hyd = '1';
      var isCard = el.classList.contains('pc');
      if (bits.sold) soldify(el, isCard);
      else if (bits.po) preorderify(el, isCard, bits.po);
      /* brand eyebrow / Brand fact: replace-only */
      if (bits.brand) {
        var vd = isCard
          ? el.querySelector('.pc-vd')
          : (function () { var f = factByLabel(el, 'Brand'); return f && f.querySelector('.v'); })();
        if (vd && vd.textContent.trim() !== bits.brand) vd.textContent = bits.brand;
      }
      if (isCard) {
        if (bits.fins) {
          /* the finish-bloom enrichment may already have parked a HIDDEN
             .pc-fins on this card (first-press fetch) - replace it with ours */
          var old = el.querySelector('.pc-fins');
          if (old) old.parentNode.removeChild(old);
          var pr = el.querySelector('.pc-pr');
          if (pr) {
            pr.insertAdjacentHTML('afterend', bits.fins);
            var nf = el.querySelector('.pc-fins');
            if (nf) nf.classList.add('asr-fins-in');
          }
        }
      } else {
        /* list row: fins between snippet and facts + the Finish fact,
           mirroring snippets/atelier-product-row.liquid's order */
        if (bits.fins && !el.querySelector('.pc-fins')) {
          var anchor = el.querySelector('.lrow-snip') || el.querySelector('.lrow-tags') || el.querySelector('.lrow-tt');
          if (anchor) {
            anchor.insertAdjacentHTML('afterend', bits.fins);
            var nf2 = el.querySelector('.pc-fins');
            if (nf2) nf2.classList.add('asr-fins-in');
          }
        }
        if (bits.fin && !factByLabel(el, 'Finish')) {
          var facts = el.querySelector('.lrow-facts');
          if (!facts) {
            var bd = el.querySelector('.lrow-bd');
            if (bd) { bd.insertAdjacentHTML('beforeend', '<div class="lrow-facts"></div>'); facts = el.querySelector('.lrow-facts'); }
          }
          if (facts) {
            var after = factByLabel(el, 'Brand');
            if (after) after.insertAdjacentHTML('afterend', finFactHTML(bits));
            else facts.insertAdjacentHTML('afterbegin', finFactHTML(bits));
          }
        }
      }
    });
  }
  /* BUFFERED application for async fetch completions. Applying each card the
     moment its fetch lands = one mutation batch per card, and the Restock
     Rocket app re-scans AND re-fetches product .js per batch (its per-handle
     dedupe only holds within one scan pass - measured 241 RR fetches/load
     unbatched vs ~94 on the server-rendered collection grid). Buffering into
     ONE DOM pass gives RR one pass, like a collection page. Cache-path
     applications don't buffer - they already run in one synchronous loop. */
  var hydBuf = [], hydFlushT = null;
  function hydFlush() {
    if (hydFlushT) { clearTimeout(hydFlushT); hydFlushT = null; }
    var buf = hydBuf; hydBuf = [];
    buf.forEach(function (b) { hydApplyNow(b.h, b.bits); });
  }
  function hydApplyBuffered(handle, bits) {
    hydBuf.push({ h: handle, bits: bits });
    if (hydBuf.length >= 8 || (!hydQueue.length && hydActive === 0)) hydFlush();
    else if (!hydFlushT) hydFlushT = setTimeout(hydFlush, 1200);
  }
  function hydFetch(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '?section_id=atelier-wishlist-card', { headers: { Accept: 'text/html' } })
      .then(function (r) {
        if (r.status === 429) return { rate: 1 };
        return !r.ok
          ? { fins: '', brand: '', fin: '', nf: 0, err: 1 }
          : r.text().then(function (html) { return html ? hydExtract(html) : { fins: '', brand: '', fin: '', nf: 0, err: 1 }; });
      })
      .catch(function () { return { fins: '', brand: '', fin: '', nf: 0, err: 1 }; });
  }
  /* TRICKLE dispatcher, deliberately gentle: max HYD_MAX in flight AND one new
     fetch per HYD_SPACE ms. A burst here rate-limits the whole storefront
     session (dev-probed 2026-07-17: sustained bursts drew 429s that broke the
     Swishlist app's own /products/<handle>.js fetch = dead-looking hearts).
     A 429'd handle backs the queue off ~4s and retries ONCE, uncached. */
  function hydPump() {
    if (hydTimer || hydActive >= HYD_MAX || !hydQueue.length) return;
    var h = hydQueue.shift();
    hydActive++;
    hydFetch(h).then(function (bits) {
      hydActive--;
      if (bits && bits.rate) {
        if (!hydRetried[h]) { hydRetried[h] = 1; hydQueue.push(h); }
        else delete hydPending[h];
        if (hydTimer) clearTimeout(hydTimer);
        hydTimer = setTimeout(function () { hydTimer = null; hydPump(); }, 4000);
        return;
      }
      hydCachePut(h, bits);
      hydApplyBuffered(h, bits);
      delete hydPending[h];
      hydPump();
    });
    if (hydQueue.length) hydTimer = setTimeout(function () { hydTimer = null; hydPump(); }, HYD_SPACE);
  }
  function hydEnqueue(handle) {
    if (!handle || hydPending[handle]) return;
    var c = hydCacheGet(handle);
    if (c) { hydApplyNow(handle, c); return; }
    hydPending[handle] = 1;
    hydQueue.push(handle);
    hydPump();
  }
  /* observe freshly rendered cards/rows; hydrate as they near the viewport.
     Cache hits apply IMMEDIATELY (a refine or back-restore paints with dots).
     The hidden view's twin never intersects, but hydApply reaches it by
     handle, so both views fill from the one fetch. */
  function hydrateNewCards() {
    /* strips overtake the et-bg underlay (shared engine; safe if absent) */
    if (window.ATEdgeTint) ATEdgeTint.scan(gridEl);
    if (hydIO) hydIO.disconnect();
    var els = gridEl.querySelectorAll('[data-handle]:not([data-hyd])');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(els, function (el) { hydEnqueue(el.getAttribute('data-handle')); });
      return;
    }
    hydIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        hydIO.unobserve(en.target);
        hydEnqueue(en.target.getAttribute('data-handle'));
      });
    }, { rootMargin: '900px 0px' });
    Array.prototype.forEach.call(els, function (el) {
      var c = hydCacheGet(el.getAttribute('data-handle'));
      if (c) hydApplyNow(el.getAttribute('data-handle'), c);
      else hydIO.observe(el);
    });
  }

  /* ---------- card finish-dot hover image-swap (ported from
     atelier-collection.js - /search doesn't load that controller) ---------- */
  gridEl.addEventListener('mouseover', function (e) {
    var dot = e.target.closest('.pc-fin');
    if (!dot || !gridEl.contains(dot)) return;
    var fimg = dot.getAttribute('data-fimg');
    if (!fimg) return;
    var card = dot.closest('.pc');
    var img = card && card.querySelector('.pc-im img.main');
    if (!img) return;
    if (!img.dataset.mainSrc) img.dataset.mainSrc = img.getAttribute('src');
    img.setAttribute('src', fimg);
  });
  gridEl.addEventListener('mouseout', function (e) {
    /* restore when the mouse leaves the swatch ROW (.pc-fins) in ANY direction;
       skip only while still inside the SAME row (kills the between-swatch
       flash) - full rationale in atelier-collection.js */
    var fromFins = e.target.closest && e.target.closest('.pc-fins');
    if (!fromFins || !gridEl.contains(fromFins)) return;
    var toFins = (e.relatedTarget && e.relatedTarget.closest) ? e.relatedTarget.closest('.pc-fins') : null;
    if (toFins === fromFins) return;
    var card = fromFins.closest('.pc');
    var img = card && card.querySelector('.pc-im img.main');
    if (img && img.dataset.mainSrc) img.setAttribute('src', img.dataset.mainSrc);
  });

  /* ---------- wishlist safety net (uninitialized-button window) ----------
     The Swishlist app re-binds fresh buttons via a scan that can lag a grid
     re-render by a few seconds. In that window a click still fires the toast
     engine's optimistic reward but commits NOTHING (dev-probed 2026-07-17:
     the app then strips the optimistic class when it re-seeds). So: when a
     clicked heart lacks the app's st-initialized marker, commit through the
     SWishlist API ourselves - keyed off OUR saved-set, NOT the button class
     (the toast engine flips the class optimistically before this bubbles).
     Once st-initialized is present the app owns the click and we stand down. */
  var wishSaved = {};
  (function seedWishSaved(n) {
    var sw = window.SWishlist;
    if (sw && typeof sw.getList === 'function') {
      try {
        Promise.resolve(sw.getList()).then(function (l) {
          var a = Array.isArray(l) ? l : (l && l.products) || [];
          a.forEach(function (it) { var h = it.handle || it.productHandle; if (h) wishSaved[h] = 1; });
        }).catch(function () {});
      } catch (e) {}
      return;
    }
    if (n < 40) setTimeout(function () { seedWishSaved(n + 1); }, 250);
  })(0);
  /* keep the set truthful after app-owned commits too (event payloads vary
     across app builds, so re-pull the list instead of trusting them) */
  var wishSyncT = null;
  function wishResync() {
    if (wishSyncT) clearTimeout(wishSyncT);
    wishSyncT = setTimeout(function () {
      var sw = window.SWishlist;
      if (!(sw && sw.getList)) return;
      try {
        Promise.resolve(sw.getList()).then(function (l) {
          var a = Array.isArray(l) ? l : (l && l.products) || [];
          wishSaved = {};
          a.forEach(function (it) { var h = it.handle || it.productHandle; if (h) wishSaved[h] = 1; });
        }).catch(function () {});
      } catch (e) {}
    }, 1200);
  }
  ['swishlist:st-added-product', 'swishlist:st-deleted-product', 'st-added-product', 'st-deleted-product'].forEach(function (n) {
    document.addEventListener(n, wishResync);
    window.addEventListener(n, wishResync);
  });
  gridEl.addEventListener('click', function (e) {
    var btn = e.target.closest('.pc-wish.st-wishlist-button');
    if (!btn || !gridEl.contains(btn)) return;
    if (btn.classList.contains('st-initialized')) return;   // app-bound: it commits
    var h = btn.getAttribute('data-handle');
    var sw = window.SWishlist;
    if (!h || !(sw && sw.addToWishlist)) return;
    var adding = !wishSaved[h];
    try {
      if (adding) { sw.addToWishlist(h); wishSaved[h] = 1; }
      else { sw.removeFromWishlist(h); delete wishSaved[h]; }
    } catch (err) { /* the app's remove path can throw a cosmetic UI error after committing */ }
  });

  /* ---- facet rail ---- */

  /* Brand logo plates (.fbr - the collection rail's A2c spec, fb99884): logo
     URLs are Liquid-rendered by the section into [data-asr-brand-logos] via
     snippets/atelier-brand-logo (SVG -> legacy PNG -> metaobject tiers). A
     brand with no asset falls back to the plain checkbox row - the registry
     philosophy, same as the collection rail. */
  var BRAND_LOGOS = (function () {
    var el = document.querySelector('[data-asr-brand-logos]');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  })();
  /* canonical brand -> asset key where handleize alone doesn't land on it */
  var BRAND_KEY_ALIAS = { greens: 'greens-tapware', ikon: 'ikon-tapware' };
  function brandKey(name) {
    var k = String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return BRAND_KEY_ALIAS[k] || k;
  }
  function brandLogo(name) { return BRAND_LOGOS[brandKey(name)] || ''; }

  /* Finish swatch map: label -> {fam, hex}, Liquid-rendered by the section
     from the colours metaobjects THROUGH the shared atelier-finish-family
     resolver - the same cascade the collection rail uses, so both rails agree
     on what family a finish belongs to (no JS re-implementation to drift).
     fam -> .ac-sw--<fam> metal gradient; no fam -> flat hex; neither -> bare
     ring dot. */
  var FINISH_MAP = (function () {
    var el = document.querySelector('[data-asr-finish-map]');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  })();
  /* Generic-tier shape icons, Liquid-rendered by the section from
     snippets/atelier-shape-icon (category:'') into template nodes - ONE
     registry file owns the SVG paths. Mixed-category search always uses the
     generic tier by design (a bath-styled glyph must never label a mirror's
     Rectangular). Miss -> plain checkbox row. */
  var SHAPE_ICONS = (function () {
    var map = {};
    document.querySelectorAll('[data-asr-shape-icon]').forEach(function (el) {
      map[el.getAttribute('data-asr-shape-icon')] = el.innerHTML;
    });
    return map;
  })();
  function shapeIcon(label) {
    return SHAPE_ICONS[String(label || '').toLowerCase().trim().replace(/\s+/g, '-')] || '';
  }

  /* One row per facet value. stateKey = the state.f Set this row ticks into
     (also what the delegated change handler reads via data-asr-facet).
     Styles: 'finish' = .fsw metal-swatch rows, 'shape' = .fsh micro-cut icon
     rows (registry miss -> plain row), brand rows get .fbr logo plates, and
     everything else is the .fopt checkbox row. Markup mirrors
     snippets/atelier-facet-options.liquid - keep in sync. */
  /* numeric key for 'num' value-sorting: leading number in the label;
     "Over 1700mm" sorts just past 1700, "Under 350mm" just before 350;
     no number at all -> the end */
  function numOf(k) {
    var m = /(\d+(?:\.\d+)?)/.exec(k);
    if (!m) return Infinity;
    var s = String(k).trim();
    return +m[1] + (/^over/i.test(s) ? 0.5 : (/^under/i.test(s) ? -0.5 : 0));
  }
  function optionRows(counts, base, stateKey, style, vsort) {
    /* base = the query's unfiltered universe: keys + ORDER come from it
       (frozen per query, count-desc groups sort by UNIVERSE counts), the
       current counts overlay it. A value with no matches under the current
       filters renders greyed + disabled in place (.dis - same rule as the
       /collections rail: count 0 AND not active) - never removed, never
       reordered. A checked value keeps working at count 0 so the user can
       always untick their way back. */
    counts = counts || {};
    base = base || counts;
    var keys = Object.keys(base);
    Object.keys(counts).forEach(function (k) { if (!(k in base)) keys.push(k); });
    if (vsort === 'num') keys.sort(function (a, b) { return numOf(a) - numOf(b); });
    else if (vsort === 'alpha') keys.sort(function (a, b) { return a.localeCompare(b); });
    else keys.sort(function (a, b) { return (base[b] || 0) - (base[a] || 0); });
    var sel = state.f[stateKey];
    return keys.map(function (k, i) {
      var n = counts[k] || 0;
      var checked = sel.has(k);
      var dis = (n === 0 && !checked) ? ' dis' : '';
      var extra = (!checked && i >= 8) ? ' x-extra' : '';
      var onCls = (checked ? ' on' : '') + dis;
      var input = '<input type="checkbox" data-asr-facet="' + stateKey + '" data-asr-val="' + esc(k) + '"' + (checked ? ' checked' : '') + (dis ? ' disabled' : '') + '>';
      var count = '<span class="ct">' + n + '</span>';
      var lbl = '<span class="lbl">' + esc(k) + '</span>';
      if (style === 'finish') {
        var fin = FINISH_MAP[k] || {};
        var dot = fin.fam
          ? '<span class="dot ac-sw--' + esc(fin.fam) + '"></span>'
          : (fin.hex ? '<span class="dot" style="background:' + esc(fin.hex) + '"></span>' : '<span class="dot"></span>');
        return '<label class="fsw' + onCls + extra + '">' + input + dot + lbl + count + '</label>';
      }
      if (style === 'shape') {
        var ic = shapeIcon(k);
        if (ic) {
          return '<label class="fsh' + onCls + extra + '">' + input +
            '<span class="sic">' + ic + '</span>' + lbl + count + '</label>';
        }
        /* registry miss falls through to the plain checkbox row */
      }
      if (stateKey === 'vendor') {
        var logo = brandLogo(k);
        if (logo) {
          /* .fbr plate: the img is absolutely positioned inside the fixed .blg box
             by the shared CSS - layout never asks the SVG its size (intrinsic-size rule) */
          return '<label class="fbr' + onCls + extra + '">' + input +
            '<span class="blg"><img src="' + esc(logo) + '" alt="" loading="lazy"></span>' +
            lbl + count + '</label>';
        }
      }
      return '<label class="fopt' + onCls + extra + '">' + input +
        '<span class="bx">' + SVG_CHECK + '</span>' + lbl + count + '</label>';
    }).join('');
  }

  /* Group header - the shipped collection spec (beb1fa2): ink eyebrow label +
     crimson active-count chip (only when the group has active filters) + rule
     line to the chevron. Markup mirrors snippets/atelier-facets.liquid - keep
     in sync; atelier-collection.css only styles these spans (the pre-redesign
     bare-label markup rendered unstyled from 2026-08-17 until this fix). The
     rail fully re-renders every run(), so counts are set at render time. */
  function headerHTML(label, act, open) {
    return '<button class="fg-q" type="button" aria-expanded="' + (open ? 'true' : 'false') + '">' +
      '<span class="t">' + label + '</span>' +
      '<span class="act' + (act > 0 ? ' show' : '') + '">' + act + '</span>' +
      '<span class="rule"></span>' +
      '<span class="ic">' + SVG_CHEV + '</span>' +
    '</button>';
  }

  function listGroup(key, label, counts, base, stateKey, style, vsort) {
    /* group presence follows the UNIVERSE: a group whose values all zero out
       under the current filters stays visible (all greyed); a group the query
       never had stays absent (per-query self-omission, unchanged). */
    var keys = Object.keys(base || counts || {});
    if (!keys.length) return '';
    var open = state.open[key];
    var showAll = state.showAll[key];
    var more = keys.length > 8
      ? '<button type="button" class="fg-more" data-asr-more-toggle="' + key + '">' +
          (showAll ? 'Show less' : 'Show all (' + keys.length + ')') + '</button>'
      : '';
    var bdCls = style === 'finish' ? ' fswatches' : '';
    return '<div class="fgroup js-filter' + (open ? ' on' : '') + (showAll ? ' show-all' : '') + '" data-asr-group="' + key + '">' +
      headerHTML(label, state.f[stateKey] ? state.f[stateKey].size : 0, open) +
      '<div class="fg-a"><div><div class="fg-bd' + bdCls + '"><div class="facets-wrap">' + optionRows(counts, base, stateKey, style, vsort) + '</div>' + more + '</div></div></div>' +
    '</div>';
  }

  /* ---- price slider (the collection rail's fp- component, 4ddd091 spec) ----
     Rebuilt here without the facets.js contract: this rail re-renders every
     run(), so the slider is emitted fresh with the committed values and wired
     by wirePriceSlider() - commits go straight into state + run(true).
     Bounds come FREE from Algolia facets_stats.price (no floor-fetch hack).
     niceStep mirrors atelier-collection.js - keep the two in sync. */
  function niceStep(span) {
    var raw = span / 50;
    var pow = Math.pow(10, Math.floor(Math.log10(raw)));
    var steps = [1, 2, 2.5, 5, 10];
    for (var i = 0; i < steps.length; i++) {
      if (raw <= steps[i] * pow) return steps[i] * pow;
    }
    return 10 * pow;
  }
  /* Track bounds rule (universe era): called ONLY at universe capture, once
     per query, with the query's UNFILTERED sticker-price stats - so the track
     re-bands on a new query, then holds still through every filter refinement
     (it can never narrow to a filter's own subset because the stats were
     never filtered). Cold loads with min/max in the URL widen the track to
     contain the committed values.
     NOTE: stats are post-distinct (afd), so the raw min can sit above a card's
     "From" price - absorbed by flooring the low bound to the step. */
  function updatePriceBounds(stats) {
    var hasFilter = state.f.priceMin != null || state.f.priceMax != null;
    if (!stats) { state.priceBounds = null; return; }
    var min = stats.min, max = stats.max;
    if (hasFilter) {
      if (state.f.priceMin != null) min = Math.min(min, state.f.priceMin);
      if (state.f.priceMax != null) max = Math.max(max, state.f.priceMax);
    }
    if (!(max > min)) { state.priceBounds = null; return; }
    var step = niceStep(max - min);
    state.priceBounds = {
      lo: Math.floor(min / step) * step,
      hi: Math.ceil(max / step) * step,
      step: step
    };
  }
  function priceGroup() {
    var b = state.priceBounds;
    if (!b) return '';   // single-price or empty set: a range filter is meaningless
    var open = state.open.price;
    var act = (state.f.priceMin != null || state.f.priceMax != null) ? 1 : 0;
    var snap = function (v) { return Math.min(b.hi, Math.max(b.lo, Math.round(v / b.step) * b.step)); };
    var va = state.f.priceMin != null ? snap(state.f.priceMin) : b.lo;
    var vb = state.f.priceMax != null ? snap(state.f.priceMax) : b.hi;
    if (vb - va < b.step) va = Math.max(b.lo, vb - b.step);
    var nSteps = Math.round((b.hi - b.lo) / b.step);
    return '<div class="fgroup js-filter' + (open ? ' on' : '') + '" data-asr-group="price">' +
      headerHTML('Price', act, open) +
      '<div class="fg-a"><div><div class="fg-bd">' +
        '<div class="fp-slider fp-ready" data-asr-price>' +
          '<div class="fp-read" data-fp-read></div>' +
          '<div class="fp-sl">' +
            '<div class="fp-track"></div><div class="fp-fill" data-fp-fill></div>' +
            '<input type="range" class="fp-min" data-fp-min min="' + b.lo + '" max="' + b.hi + '" step="' + b.step + '" value="' + va + '" aria-label="Minimum price">' +
            '<input type="range" class="fp-max" data-fp-max min="' + b.lo + '" max="' + b.hi + '" step="' + b.step + '" value="' + vb + '" aria-label="Maximum price">' +
          '</div>' +
          '<div class="fp-ticks" style="background-size:calc(100% / ' + nSteps + ') 100%"></div>' +
        '</div>' +
      '</div></div></div>' +
    '</div>';
  }
  function wirePriceSlider() {
    var el = railBox.querySelector('[data-asr-price]');
    var b = state.priceBounds;
    if (!el || !b) return;
    var minI = el.querySelector('[data-fp-min]');
    var maxI = el.querySelector('[data-fp-max]');
    var read = el.querySelector('[data-fp-read]');
    var fill = el.querySelector('[data-fp-fill]');
    /* geometry constants (9px inset / 18px thumb) match the shared .fp- CSS -
       track/fill/ticks are drawn against thumb-CENTRE travel */
    var upd = function (mover, commit) {
      var va = +minI.value, vb = +maxI.value;
      if (vb - va < b.step) {   // min-gap: thumbs can never meet (one full step)
        if (mover === minI) { va = Math.max(b.lo, vb - b.step); minI.value = va; }
        else { vb = Math.min(b.hi, va + b.step); maxI.value = vb; }
      }
      read.textContent = aud(va) + ' – ' + aud(vb);
      var f1 = (va - b.lo) / (b.hi - b.lo), f2 = (vb - b.lo) / (b.hi - b.lo);
      fill.style.left = 'calc(9px + (100% - 18px) * ' + f1 + ')';
      fill.style.right = 'calc(9px + (100% - 18px) * ' + (1 - f2) + ')';
      minI.classList.toggle('fp-top', va > b.lo + (b.hi - b.lo) * 0.6);
      if (commit) {
        var mn = va > b.lo ? va : null;
        var mx = vb < b.hi ? vb : null;
        if (mn === state.f.priceMin && mx === state.f.priceMax) return;
        state.f.priceMin = mn;
        state.f.priceMax = mx;
        run(true);
      }
    };
    minI.addEventListener('input', function () { upd(minI, false); });
    maxI.addEventListener('input', function () { upd(maxI, false); });
    minI.addEventListener('change', function () { upd(minI, true); });
    maxI.addEventListener('change', function () { upd(maxI, true); });
    upd(minI, false);
  }

  /* countsBy = {category:{}, brand:{}, <mfacet key>:{}} - assembled in run()
     from request 0 (unselected groups) + the per-group exclusion requests. */
  function renderRail(countsBy, baseBy) {
    /* baseBy = the universe counts for this query (null when none applies -
       unfiltered runs pass null and each group is its own base). An EMPTY
       universe group falls back to the current counts, so a data-drift value
       appearing only under filters can never hide a whole group. */
    baseBy = baseBy || {};
    var baseOf = function (cur, b) { return (b && Object.keys(b).length) ? b : cur; };
    var groups = RAIL_ORDER.map(function (k) {
      if (k === 'brand') {
        var curB = mergeBrandCounts(countsBy.brand);
        return listGroup('brand', 'Brand', curB, baseOf(curB, mergeBrandCounts(baseBy.brand)), 'vendor', 'brand');
      }
      if (k === 'price') return priceGroup();
      var g = MFACET_BY_KEY[k];
      return g ? listGroup(g.key, g.label, countsBy[g.key], baseOf(countsBy[g.key], baseBy[g.key]), g.key, g.style, g.vsort) : '';
    }).join('');
    /* wrap in #FacetFiltersForm: the collection's mobile-drawer CSS targets
       .ac-rail #FacetFiltersForm as the flex column (fixed head/scroll/foot).
       On desktop it's just a plain block. */
    railBox.innerHTML =
      '<div id="FacetFiltersForm">' +
        '<div class="ac-fr-head"><span class="ac-fr-t">Filters</span>' +
          '<button type="button" class="ac-fr-x" data-asr-filter-close aria-label="Close filters">' + SVG_X + '</button></div>' +
        '<div class="ac-fr-scroll"><div id="FacetsWrapperDesktop">' + groups + '</div></div>' +
        '<div class="ac-fr-foot">' +
          '<button type="button" class="ac-btn ac-btn--ghost" data-asr-clear>Clear all</button>' +
          '<button type="button" class="ac-btn" data-asr-filter-close>Show results</button>' +
        '</div>' +
      '</div>';
  }

  /* ---- active-filter chips: the collection page's "Filtered by" bar (.afp*,
     snippets/atelier-active-pills.liquid is the markup reference). The old
     .apill classes were REMOVED from atelier-collection.css in the applied-
     filters redesign - unstyled chips exploded the layout (giant inline SVG). ---- */
  var SVG_FUNNEL = '<svg viewBox="0 0 24 24"><path d="M3 5h18M6 12h12M10 19h4"/></svg>';
  var SVG_CLEAR  = '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M15 9l-6 6M9 9l6 6"/></svg>';
  function pill(type, val, label, markHtml, markCls, glabel) {
    /* chips stay value-only visually (Chris 2026-08-19: group labels not
       worth the width) - the group name rides along as title/aria instead,
       so hover + screen readers still get "Shape: Egg" for free */
    var aria = glabel ? 'Remove ' + esc(glabel) + ' filter: ' + esc(label) : 'Remove filter ' + esc(label);
    var tip = glabel ? ' title="' + esc(glabel) + ': ' + esc(label) + '"' : '';
    /* >14 chars = full-row chip on the mobile grid; the WRAPPER carries
       afp-w-wide for the grid selectors (mirrors atelier-active-pills) */
    var wide = String(label).length > 14;
    return '<facet-remove' + (wide ? ' class="afp-w-wide"' : '') + '><a class="afp' + (markCls || '') + (wide ? ' afp--wide' : '') + '" href="#" data-asr-pill-type="' + type + '" data-asr-pill-val="' + esc(val) + '" aria-label="' + aria + '"' + tip + '>' +
      (markHtml || '') +
      '<span class="afp-t">' + esc(label) + '</span><span class="afp-x">' + SVG_X + '</span></a></facet-remove>';
  }
  /* chip identity marks (bar redesign 2026-08-19): finish chips carry the
     rail's metal swatch dot, shape chips the same micro-cut icon the rail
     rows use - parity with the /collections chips (atelier-active-pills). */
  function pillMark(key, v) {
    if (key === 'finish') {
      var fin = FINISH_MAP[v] || {};
      if (fin.fam) return { h: '<span class="afp-sw ac-sw--' + esc(fin.fam) + '"></span>', c: ' afp--sw' };
      if (fin.hex) return { h: '<span class="afp-sw" style="background:' + esc(fin.hex) + '"></span>', c: ' afp--sw' };
      return null;
    }
    if (key === 'shape') {
      var ic = shapeIcon(v);
      if (ic) return { h: '<span class="afp-ic">' + ic + '</span>', c: ' afp--ic' };
    }
    return null;
  }
  function renderPills() {
    var chips = '';
    arr(state.f.vendor).forEach(function (v) { chips += pill('vendor', v, v, null, null, 'Brand'); });
    MFACETS.forEach(function (g) {
      arr(state.f[g.key]).forEach(function (v) {
        var m = pillMark(g.key, v);
        chips += pill(g.key, v, v, m && m.h, m && m.c, g.label);
      });
    });
    if (state.f.priceMin != null || state.f.priceMax != null) {
      /* one-sided ranges read as words - "Up to $4,800" / "From $800" - only
         a genuine two-ended range gets the dash (mirrors atelier-active-pills,
         keep in sync). aud() is already whole dollars. */
      var pLbl;
      if (state.f.priceMin != null && state.f.priceMax != null) pLbl = aud(state.f.priceMin) + ' – ' + aud(state.f.priceMax);
      else if (state.f.priceMax != null) pLbl = 'Up to ' + aud(state.f.priceMax);
      else pLbl = 'From ' + aud(state.f.priceMin);
      chips += pill('price', '', pLbl, null, null, 'Price');
    }
    /* .afp-in wrapper = the single animatable child of .ac-active (the bar
       slides open/closed via grid-template-rows in atelier-collection.css) -
       mirrors atelier-active-pills.liquid, keep in sync */
    pillsEl.innerHTML = chips
      ? '<div class="afp-in"><div class="afp-panel"><div class="afp-head"><span class="afp-lead">' + SVG_FUNNEL + '<span class="afp-lead-t">Filtered by</span></span>' +
        '<facet-remove class="afp-clear-wrap"><a href="#" class="afp-clear" data-asr-clear aria-label="Clear all filters">' + SVG_CLEAR + 'Clear all</a></facet-remove></div>' +
        '<div class="afp-chips">' + chips + '</div></div></div>'
      : '';
  }

  /* ---- query-transparency note (under the heading) ---- */
  function setNote(html) { if (!noteEl) return; noteEl.innerHTML = html || ''; noteEl.style.display = html ? '' : 'none'; }
  function renderNote(typed, enhancedQuery) {
    if (!noteEl) return;
    var html = '';
    var cleaned = (window.BSDSearch && BSDSearch.normalize) ? BSDSearch.normalize(typed) : typed;
    if (cleaned && cleaned.toLowerCase() !== String(typed).trim().toLowerCase()) {
      html += '<span class="asr-note-line">Showing results for <b>' + esc(cleaned) + '</b></span>';
    }
    var fam = (window.BSDSearch && BSDSearch.detectFinishFamily) ? BSDSearch.detectFinishFamily(enhancedQuery) : null;
    var fams = (window.BSDSearch && BSDSearch.FINISH_FAMILIES) || {};
    if (fam && fams[fam]) {
      var f = fams[fam];
      var pick = state.finishPick, pickDisp = '';
      var chips = [f.label].concat(f.incl).map(function (m) {
        var term = m.toLowerCase(), on = pick === term;
        if (on) pickDisp = m;
        return '<button type="button" class="asr-fam-chip' + (on ? ' on' : '') + '" data-asr-finish="' + esc(term) + '">' +
          '<i class="ac-sw--' + f.swatch + '"></i>' + esc(m) + '</button>';
      }).join('');
      var head = pick
        ? 'Showing <b>' + esc(pickDisp || pick) + '</b> &middot; one of the ' + esc(f.label) + ' finishes'
        : 'Showing the full <b>' + esc(f.label) + '</b> finish family';
      var allBtn = pick ? '<button type="button" class="asr-fam-all" data-asr-finish-all>&larr; All ' + esc(f.label) + '</button>' : '';
      html += '<div class="asr-fam fam-' + esc(f.swatch) + '">' +
        '<span class="asr-fam-sw ac-sw--' + f.swatch + '"></span>' +
        '<div class="asr-fam-bd">' +
          '<span class="asr-fam-h">' + head + '</span>' +
          (pick ? '' : '<span class="asr-fam-hint">Pick a finish to narrow your results</span>') +
          '<div class="asr-fam-chips">' + chips + allBtn + '</div>' +
        '</div>' +
      '</div>';
    }
    setNote(html);
  }

  /* ---- suggestion chips + rich empty / no-results states ---- */
  function recents() { try { return JSON.parse(localStorage.getItem('bsd_recent') || '[]'); } catch (e) { return []; } }
  function chip(q) { return '<a class="asr-chip" href="/search?q=' + encodeURIComponent(q) + '">' + esc(q) + '</a>'; }
  function suggestBlocks() {
    var r = recents();
    return (r.length ? '<div class="asr-sg"><span class="asr-sg-h">Recent searches</span><div class="asr-chips">' + r.slice(0, 6).map(chip).join('') + '</div></div>' : '') +
      '<div class="asr-sg"><span class="asr-sg-h">Popular searches</span><div class="asr-chips">' + POPULAR.map(chip).join('') + '</div></div>' +
      '<div class="asr-sg"><a class="asr-browse" href="/collections">Browse all categories &rarr;</a></div>';
  }
  function renderSkeleton() {
    setStatus('');
    moreWrap.style.display = 'none';
    /* reuse the REAL .pc shell (pc-im / pc-bd / pc-act) so the skeleton is the
       exact same size + layout as a card; only the inner blocks are shimmer. */
    var one = '<div class="pc asr-sk" aria-hidden="true">' +
      '<div class="pc-im"><span class="sk-b sk-im"></span></div>' +
      '<div class="pc-bd">' +
        '<span class="sk-b sk-eye"></span>' +
        '<span class="sk-b sk-ln"></span>' +
        '<span class="sk-b sk-ln sk-ln2"></span>' +
        '<span class="sk-b sk-price"></span>' +
        '<div class="pc-act"><span class="sk-b sk-btn"></span><span class="sk-b sk-wish"></span></div>' +
      '</div>' +
    '</div>';
    gridEl.classList.add('asr-skeleton');
    gridEl.innerHTML = new Array(9).join(one);   // 8 placeholder cards
  }

  function renderEmpty(kind, q) {
    state.hasResults = false;
    gridEl.classList.remove('is-loading', 'asr-skeleton');
    gridEl.innerHTML = '';
    moreWrap.style.display = 'none';
    setNote('');
    var head, sub, clear = '';
    if (kind === 'no-results') {
      head = 'No matches for <b>&ldquo;' + esc(q) + '&rdquo;</b>';
      sub = anyFilter() ? 'Try removing a filter, or one of these:' : 'Check the spelling, try a simpler word, or one of these:';
      if (anyFilter()) clear = '<button type="button" class="ac-btn" data-asr-clear>Clear filters</button>';
    } else {
      head = 'Search the BSD range';
      sub = 'Tapware, vanities, baths and more — start with a popular search:';
    }
    setStatus('<div class="ac-empty"><div class="ac-empty-ic">' + SVG_FIND + '</div>' +
      '<h3>' + head + '</h3><p>' + sub + '</p>' + clear + '</div>' +
      '<div class="asr-suggest">' + suggestBlocks() + '</div>');
  }

  function updateMore(main) {
    var hasMore = state.page + 1 < state.nbPages;
    moreWrap.style.display = hasMore ? '' : 'none';
    var total = main.nbHits || 0;
    if (lmShown) lmShown.textContent = state.shown.toLocaleString();
    if (lmTotal) lmTotal.textContent = total.toLocaleString();
    if (lmBar) lmBar.style.width = total ? Math.min(100, Math.round(state.shown / total * 100)) + '%' : '0%';
  }

  /* ---------- URL state + instant back-restore (contained, no network on restore) ---------- */
  function parseUrlIntoState() {
    var p; try { p = new URLSearchParams(location.search); } catch (e) { p = null; }
    if (!p) { state.q = (root.getAttribute('data-q') || '').trim(); return; }
    state.q = (p.get('q') || root.getAttribute('data-q') || '').trim();
    state.f.vendor = new Set(p.getAll('brand'));
    MFACETS.forEach(function (g) { state.f[g.key] = new Set(p.getAll(g.param)); });
    var mn = p.get('min'), mx = p.get('max');
    state.f.priceMin = (mn != null && mn !== '') ? parseInt(mn, 10) : null;
    state.f.priceMax = (mx != null && mx !== '') ? parseInt(mx, 10) : null;
    state.sort = (p.get('sort') === 'bestselling') ? 'bestselling' : 'relevance';
    state.finishPick = p.get('finish') || null;
  }
  /* ---- back-restore storage: LOCALstorage, not session. iOS Safari can drop
     sessionStorage around address-bar navigations (Apple forums 724189, Safari
     14-16), which is exactly how a pasted preview link starts a session. TTL +
     key-matching bound the wider localStorage scope. ---- */
  var SNAP_TTL = 1800000; /* 30min */
  function stGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function stSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function stDel(k) { try { localStorage.removeItem(k); } catch (e) {} }
  /* ?asrdebug=1 - on-device diagnosis overlay for the restore chain. Every dbg
     line ALSO lands in a small localStorage ring log, so the OUTBOUND half of a
     round-trip (written just before navigation) can be read back on return. */
  var DBG = /(\?|&)asrdebug=1/.test(location.search);
  var dbgEl = null;
  function dbgShow(line) {
    if (!dbgEl) {
      dbgEl = document.createElement('pre');
      dbgEl.style.cssText = 'position:fixed;left:8px;bottom:8px;right:8px;z-index:999999;background:rgba(20,21,24,.92);color:#7ee0a8;font:10px/1.5 monospace;padding:8px 10px;border-radius:8px;white-space:pre-wrap;max-height:45vh;overflow:auto;pointer-events:none';
      (document.body || document.documentElement).appendChild(dbgEl);
    }
    dbgEl.textContent += line + '\n';
    dbgEl.scrollTop = dbgEl.scrollHeight; /* panel ignores touches - keep the NEWEST lines in view */
  }
  function dbg(line) {
    var stamped = new Date().toTimeString().slice(0, 8) + ' ' + line;
    try {
      var L = (localStorage.getItem('asr_dbg') || '').split('\n').filter(Boolean);
      L.push(stamped);
      localStorage.setItem('asr_dbg', L.slice(-24).join('\n'));
    } catch (e) {}
    if (DBG) dbgShow(stamped);
  }
  if (/(\?|&)asrclear=1/.test(location.search)) { stDel('asr_dbg'); stDel('asr_out'); stDel('asr_snap'); }
  if (DBG) {
    var prior = stGet('asr_dbg');
    if (prior) {
      var pl = prior.split('\n');
      if (pl.length > 10) prior = '(... ' + (pl.length - 10) + ' older lines trimmed ...)\n' + pl.slice(-10).join('\n');
      dbgShow('---- prior log ----\n' + prior + '\n---- this load ----');
    }
  }
  /* reflect current state in the URL so reload/share/back keep query+filters+sort */
  function syncUrl() {
    var p = new URLSearchParams();
    if (state.q) p.set('q', state.q);
    arr(state.f.vendor).forEach(function (v) { p.append('brand', v); });
    MFACETS.forEach(function (g) {
      arr(state.f[g.key]).forEach(function (v) { p.append(g.param, v); });
    });
    if (state.f.priceMin != null) p.set('min', state.f.priceMin);
    if (state.f.priceMax != null) p.set('max', state.f.priceMax);
    if (state.sort && state.sort !== 'relevance') p.set('sort', state.sort);
    if (state.finishPick) p.set('finish', state.finishPick);
    if (DBG) p.set('asrdebug', '1'); /* keep the debug flag through URL rewrites */
    var qs = p.toString();
    /* the {asr:1} stamp lives ON this history entry: it is how isBackNav()
       recognises a return to this exact entry on iOS (see note there) */
    try { history.replaceState({ asr: 1 }, '', location.pathname + (qs ? '?' + qs : '')); } catch (e) {}
  }
  function syncSortUI() {
    if (!sortWrap) return;
    sortWrap.querySelectorAll('.ac-adrop-opt').forEach(function (o) {
      var on = o.getAttribute('data-val') === state.sort;
      o.classList.toggle('ac-on', on);
      if (on && sortLbl) sortLbl.textContent = o.querySelector('.ac-lbl').textContent;
    });
  }
  /* snapshot the rendered view on leave, so a back-navigation restores it instantly.
     Written at CARD-CLICK time (primary - synchronous, before navigation starts)
     AND at pagehide (refresher for late scrolling) - iOS Safari is documented to
     drop storage writes made during pagehide, so pagehide can never be the only
     writer. */
  function takeSnapshot() {
    if (!state.hasResults) return;
    /* LIGHTWEIGHT: store only state + scroll + how many pages were loaded - NOT the
       rendered HTML. Serialising a 200-card grid here froze navigation for seconds
       (pagehide runs before the browser leaves). On back we re-fetch in ONE query. */
    stSet('asr_snap', JSON.stringify({
      key: keyOf(),
      t: Date.now(),
      scrollY: window.scrollY || window.pageYOffset || 0,
      view: root.getAttribute('data-ac-current-view') || 'grid',
      pages: (state.page || 0) + 1,
      st: {
        q: state.q, vd: arr(state.f.vendor),
        mf: (function () {
          var o = {};
          MFACETS.forEach(function (g) { if (state.f[g.key].size) o[g.key] = arr(state.f[g.key]); });
          return o;
        })(),
        mn: state.f.priceMin, mx: state.f.priceMax, sort: state.sort, finish: state.finishPick
      }
    }));
  }
  function isBackNav() {
    try {
      var t = '';
      var n = performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (n && n[0]) t = n[0].type;
      else if (performance.navigation) t = performance.navigation.type === 2 ? 'back_forward' : (performance.navigation.type === 1 ? 'reload' : 'navigate');
      if (t === 'back_forward') return true;
      if (t === 'reload') return false;
      /* iOS misreports non-bfcache BACK navigations as 'navigate' (WebKit
         nav-timing bug) - the reason restore worked on desktop but not iPhone.
         Fallback signal 1: the {asr:1} stamp syncUrl leaves on OUR history
         entry. A fresh navigation to /search creates a NEW entry with null
         state, so the stamp = proof of revisit. (Reloads preserve it too -
         excluded above; 'reload' IS reported reliably, and native scroll
         handling covers F5.) Worked in the Shopify-app webview. */
      if (history.state && history.state.asr === 1) return true;
      /* Fallback signal 2 (real iOS Safari): it can ALSO leave history.state
         null at init time on a non-bfcache back load, so the stamp reads empty
         even on a genuine revisit. The outbound marker (stamped the moment a
         results card is clicked, consumed single-shot in tryRestore, 30min TTL)
         needs nothing from the browser at load time. */
      var out = +stGet('asr_out') || 0;
      if (out && Date.now() - out < SNAP_TTL) return true;
      return false;
    } catch (e) { return false; }
  }
  /* fold the restore payload INTO this history entry's URL. Round-4 primary
     channel: iOS Safari came back with navtype misreported, history.state null
     AND localStorage empty (Chris's asrdebug capture) - but entry URLs are the
     one thing every browser restores faithfully on back. Written at card-click
     (+ pagehide refresh); syncUrl() naturally strips the token on restore
     because it rebuilds the URL from state. */
  function stampUrl() {
    if (!state.hasResults) return;
    try {
      var p = new URLSearchParams(location.search);
      p.set('asr', Math.round(window.scrollY || window.pageYOffset || 0) + '.' + ((state.page || 0) + 1) + '.' + ((root.getAttribute('data-ac-current-view') || 'grid') === 'list' ? 'l' : 'g'));
      history.replaceState({ asr: 1 }, '', location.pathname + '?' + p.toString());
    } catch (e) { dbg('stampUrl FAIL ' + e); }
  }
  /* canonical snapshot key: params WITHOUT our transport tokens (asr, asrdebug),
     SORTED. Chris's round-4 forensics: iOS Safari restores the PRE-replaceState
     entry URL on back (original params, original order, null state), so raw
     location.search can never be compared against a key taken after our own
     rewrites - normalise both sides. */
  function keyOf(search) {
    var p = new URLSearchParams(search == null ? location.search : search);
    p.delete('asr'); p.delete('asrdebug');
    var kv = []; p.forEach(function (v, k) { kv.push(k + '=' + v); });
    kv.sort();
    return location.pathname + '?' + kv.join('&');
  }
  /* restore the snapshot on back (matching URL) - no Algolia call, just re-inject + scroll */
  function tryRestore() {
    var nt = ''; try { var nn = performance.getEntriesByType('navigation'); nt = nn && nn[0] ? nn[0].type : '(none)'; } catch (eD) { nt = '(err)'; }
    dbg('IN  ' + location.origin + ' navtype=' + nt + ' state=' + JSON.stringify(history.state) + ' marker=' + (stGet('asr_out') || 'none') + ' ref=' + document.referrer);
    /* path 1: URL-carried payload - immune to every iOS storage/history quirk */
    var tok = new URLSearchParams(location.search).get('asr');
    if (tok) {
      var m = /^(\d+)\.(\d+)\.(l|g)$/.exec(tok);
      dbg('url token=' + tok + (m ? ' -> RESTORING' : ' (malformed - ignored)'));
      if (m) {
        if (inputEl) inputEl.value = state.q;
        applyView(m[3] === 'l' ? 'list' : 'grid');
        syncSortUI();
        run(true, { pages: Math.max(1, +m[2] || 1), scrollY: +m[1] || 0 });
        return true;
      }
    }
    /* path 2: storage snapshot behind the three-signal detector */
    var back = isBackNav();
    /* single-shot: the outbound marker is spent the moment it is judged,
       whatever the outcome - it must never trigger a second restore later */
    stDel('asr_out');
    dbg('isBackNav=' + back);
    if (!back) return false;
    var raw = stGet('asr_snap');
    if (!raw) { dbg('no snapshot'); return false; }
    var snap; try { snap = JSON.parse(raw); } catch (e) { dbg('snapshot unparseable'); return false; }
    if (!snap) return false;
    if (snap.t && Date.now() - snap.t > SNAP_TTL) { dbg('snapshot expired'); return false; }
    if (snap.key !== keyOf()) {
      dbg('key mismatch\n  saved  ' + snap.key + '\n  actual ' + keyOf());
      return false;
    }
    dbg('RESTORING pages=' + snap.pages + ' scrollY=' + snap.scrollY);
    var s = snap.st;
    state.q = s.q; state.sort = s.sort || 'relevance'; state.finishPick = s.finish || null;
    state.f.vendor = new Set(s.vd || []);
    MFACETS.forEach(function (g) { state.f[g.key] = new Set((s.mf && s.mf[g.key]) || []); });
    state.f.priceMin = (s.mn == null ? null : s.mn); state.f.priceMax = (s.mx == null ? null : s.mx);
    if (inputEl) inputEl.value = state.q;
    applyView(snap.view === 'list' ? 'list' : 'grid');
    syncSortUI();
    /* re-fetch all the loaded pages in ONE query, then jump to the saved scroll */
    run(true, { pages: Math.max(1, snap.pages || 1), scrollY: snap.scrollY || 0 });
    return true;
  }

  /* ---- run a fresh search (hits + disjunctive facet counts).
     restore = { pages, scrollY } when rebuilding the view on a back-navigation. ---- */
  function run(reset, restore) {
    if (reset) { state.page = 0; state.shown = 0; autoLoads = 0; }
    var q = state.q;
    if (termEl) termEl.textContent = q;
    if (forEl) forEl.style.display = q ? '' : 'none';
    if (!q) {
      gridEl.innerHTML = ''; railBox.innerHTML = ''; pillsEl.innerHTML = '';
      moreWrap.style.display = 'none';
      if (countEl) countEl.textContent = '0';
      renderEmpty('no-query');
      return;
    }
    syncUrl();
    /* loading indication: cold start -> skeleton placeholders; refine -> dim the
       existing cards in place (a text block would shove the grid down and back). */
    if (state.hasResults) gridEl.classList.add('is-loading');
    else renderSkeleton();
    var A = conf();
    var bq = buildQuery();
    /* the note is query-derived (finish family + cleaned text), NOT result-derived -
       render it NOW so its height is reserved during the skeleton phase and the
       cards don't jump down when the note appears alongside the results. */
    renderNote(q, bq.famQuery);
    var enh = bq.enh;
    /* DISJUNCTIVE SCHEME (Stage C): request 0 = hits + ALL facet counts +
       variants_min_price stats under the FULL filter set - counts are correct
       for every group with no selections of its own. Each group that HAS
       selections gets one extra hpp=0 request excluding its own filters (so
       ticking a value never zeroes its siblings). Typical cost: 1 sub-request
       unfiltered, +1 per actively-filtered group - cheaper than the old flat 3. */
    var allFacets = ['vendor', 'variants_min_price'].concat(
      MFACETS.map(function (g) { return g.attr; })
    );
    var exGroups = [];
    if (state.f.vendor.size) exGroups.push({ key: 'brand', attr: 'vendor' });
    MFACETS.forEach(function (g) { if (state.f[g.key].size) exGroups.push({ key: g.key, attr: g.attr }); });
    var reqs = [
      { indexName: sortIndex(A), params: buildParams({ query: enh.q, hpp: restore ? Math.min(1000, PER * restore.pages) : PER, page: 0, facets: allFacets, facetFilters: ffArr(null), intentFF: enh.ff, numeric: numArr(), optionalFilters: enh.of, maxValuesPerFacet: 300, afd: true }) }
    ];
    exGroups.forEach(function (g) {
      reqs.push({ indexName: A.prod, params: buildParams({ query: enh.q, hpp: 0, facets: [g.attr], facetFilters: ffArr(g.key), intentFF: enh.ff, numeric: numArr(), maxValuesPerFacet: 300, afd: true }) });
    });
    /* UNIVERSE side request (grey-out union + frozen order): the query's
       UNFILTERED facet lists + sticker-price stats. Only needed when filters
       are active AND this query's universe isn't cached yet - i.e. a cold
       load of a filtered URL. The common path (land unfiltered, then refine)
       captures it for free from request 0. hpp=0, one per query per session. */
    var needUni = anyFilter() && !(state.uni && state.uni.q === enh.key);
    if (needUni) {
      reqs.push({ indexName: A.prod, params: buildParams({ query: enh.q, hpp: 0, facets: allFacets, intentFF: enh.ff, maxValuesPerFacet: 300, afd: true }) });
    }
    /* intent extras ride last in the batch (same user filters + boosts) */
    var exAt = reqs.length;
    enh.extras.forEach(function (x) {
      reqs.push({ indexName: sortIndex(A), params: buildParams({ query: x.query, hpp: PER, page: 0, facetFilters: ffArr(null), numeric: numArr(), optionalFilters: enh.of, filters: x.filters, strict: true,
        /* size extras put the number last: prefix-match it so "1500" finds
           "1500mm" titles without relying on width data */
        queryType: x.prefixLast && BSDSearch.EXTRA_PREFIX_LAST }) });
    });
    var mySeq = ++state.seq;
    state.loading = true;
    fetchA(reqs).then(function (results) {
      if (mySeq !== state.seq) return;
      state.loading = false;
      var main = results[0];
      var hits = main.hits || [];
      /* intent extras: products the size/format filter would have excluded but
         whose text matches - appended after the first page (disjoint by
         construction; de-duplicated across extras), counted in the total */
      var extraHits = [], extraN = 0, seenX = {};
      for (var xi = exAt; xi < results.length; xi++) {
        extraN += results[xi].nbHits || 0;
        (results[xi].hits || []).forEach(function (h) { if (!seenX[h.id]) { seenX[h.id] = 1; extraHits.push(h); } });
      }
      if (extraHits.length) hits = hits.concat(extraHits);
      /* on restore we fetched many pages in one query - set pagination in PER terms
         so "Load more" keeps working from where they were. */
      if (restore) { state.page = Math.max(0, restore.pages - 1); state.nbPages = Math.max(1, Math.ceil((main.nbHits || 0) / PER)); }
      else { state.page = main.page; state.nbPages = main.nbPages; }
      state.shown = hits.length;
      gridEl.classList.remove('is-loading', 'asr-skeleton');
      if (countEl) countEl.textContent = ((main.nbHits || 0) + extraN).toLocaleString();
      /* assemble per-group counts: request 0 for unselected groups, the
         exclusion requests override for selected ones */
      var f0 = main.facets || {};
      var countsBy = { brand: f0.vendor || {} };
      MFACETS.forEach(function (g) { countsBy[g.key] = f0[g.attr] || {}; });
      exGroups.forEach(function (g, i) {
        var r = results[1 + i];
        if (r && r.facets && r.facets[g.attr]) {
          if (g.key === 'brand') countsBy.brand = r.facets[g.attr];
          else countsBy[g.key] = r.facets[g.attr];
        }
      });
      /* universe capture: from the side request on a cold filtered load,
         else (unfiltered run) request 0 IS the universe. Cached per enh.key. */
      var pStats = main.facets_stats && main.facets_stats.variants_min_price;
      var uniCaptured = false;
      if (needUni) {
        var ur = results[1 + exGroups.length];
        if (ur && ur.facets) {
          var uc = { brand: ur.facets.vendor || {} };
          MFACETS.forEach(function (g) { uc[g.key] = ur.facets[g.attr] || {}; });
          state.uni = { q: enh.key, counts: uc, stats: ur.facets_stats && ur.facets_stats.variants_min_price };
          uniCaptured = true;
        }
      } else if (!anyFilter()) {
        state.uni = { q: enh.key, counts: countsBy, stats: pStats };
        uniCaptured = true;
      }
      /* slider track bounds from STICKER-price stats (variants_min_price -
         matches what the filter means since the sticker-semantics fix).
         Set ONCE per query at universe capture, from UNFILTERED stats: the
         track re-bands on a new query, then holds still while filters are
         refined - it never narrows to other groups' selections and never
         disappears on a 0-result filter state (rail-stability doctrine). */
      if (uniCaptured) updatePriceBounds(state.uni.stats);
      renderRail(countsBy, (state.uni && state.uni.q === enh.key) ? state.uni.counts : null);
      wirePriceSlider();
      renderPills();
      if (!hits.length) { renderEmpty('no-results', q); }
      else {
        gridEl.innerHTML = hits.map(itemHTML).join(''); setStatus(''); state.hasResults = true;
        hydrateNewCards();
        sizeLinks(hits, enh.size);
        if (restore) {
          /* instant content + instant jump (override global smooth-scroll) */
          Array.prototype.forEach.call(gridEl.querySelectorAll('.asr-enter'), function (el) { el.classList.remove('asr-enter'); });
          var de = document.documentElement, prevSB = de.style.scrollBehavior, y = restore.scrollY || 0;
          de.style.scrollBehavior = 'auto';
          requestAnimationFrame(function () { window.scrollTo(0, y); requestAnimationFrame(function () { window.scrollTo(0, y); de.style.scrollBehavior = prevSB; }); });
          if (DBG) setTimeout(function () {
            dbg('post-restore: target=' + y + ' actual=' + Math.round(window.scrollY) + ' docH=' + document.documentElement.scrollHeight);
          }, 700);
        } else { settleStagger(); }
      }
      updateMore(main);
    }).catch(function (err) {
      if (err.name === 'AbortError') return;
      state.loading = false;
      gridEl.classList.remove('is-loading');
      setStatus('Search is temporarily unavailable. <a href="#" data-asr-retry>Try again</a>.');
    });
  }

  function loadMore() {
    if (state.loading || state.page + 1 >= state.nbPages) return;
    var A = conf();
    var enh = buildQuery().enh;
    var reqs = [{ indexName: sortIndex(A), params: buildParams({ query: enh.q, hpp: PER, page: state.page + 1, facetFilters: ffArr(null), intentFF: enh.ff, numeric: numArr(), optionalFilters: enh.of }) }];
    var mySeq = ++state.seq;
    state.loading = true;
    moreBtn.disabled = true;
    moreBtn.textContent = 'Loading...';
    fetchA(reqs).then(function (results) {
      if (mySeq !== state.seq) return;
      state.loading = false;
      var main = results[0];
      state.page = main.page; state.nbPages = main.nbPages;
      var hits = main.hits || [];
      gridEl.insertAdjacentHTML('beforeend', hits.map(itemHTML).join(''));
      state.shown += hits.length;
      hydrateNewCards();
      sizeLinks(hits, enh.size);
      updateMore(main);
      settleStagger();
      moreBtn.disabled = false;
      moreBtn.textContent = 'Load more';
      pumpAuto();   /* short pages: keep auto-filling until the zone clears or the cap */
    }).catch(function () { state.loading = false; moreBtn.disabled = false; moreBtn.textContent = 'Load more'; });
  }

  /* ---- mobile drawer (reuses the collection's .ac-rail.ac-open + scrim CSS) ---- */
  function openDrawer() {
    if (railBox) railBox.classList.add('ac-open');
    if (scrimEl) scrimEl.classList.add('ac-on');
    document.body.classList.add('ac-drawer-lock');
  }
  function closeDrawer() {
    if (railBox) railBox.classList.remove('ac-open');
    if (scrimEl) scrimEl.classList.remove('ac-on');
    document.body.classList.remove('ac-drawer-lock');
  }

  /* ---- grid/list view toggle (reuses the collection's [data-ac-current-view]
     CSS for show/hide; we only switch the grid columns in atelier-search.css) ---- */
  function applyView(v) {
    root.setAttribute('data-ac-current-view', v);
    if (densityWrap) densityWrap.querySelectorAll('button[data-view]').forEach(function (b) {
      b.classList.toggle('ac-on', b.getAttribute('data-view') === v);
    });
    /* hydration skips hidden view-twins - top the newly-visible ones up
       (cache hits fill synchronously; anything uncached re-queues) */
    hydrateNewCards();
  }

  /* ---- interactions (delegated; rail re-renders each query). Price is
     slider-only (Chris's call on the collection rail) - the slider's own
     change listeners in wirePriceSlider() commit it, nothing delegated. ---- */
  root.addEventListener('change', function (e) {
    var cb = e.target.closest('input[data-asr-facet]');
    if (cb) {
      var attr = cb.getAttribute('data-asr-facet');
      var val = cb.getAttribute('data-asr-val');
      var set = state.f[attr];
      if (cb.checked) set.add(val); else set.delete(val);
      run(true);
    }
  });

  root.addEventListener('click', function (e) {
    /* finish-family chip: narrow to one finish (click again, or "All", to pop back) */
    var fc = e.target.closest('.asr-fam-chip[data-asr-finish]');
    if (fc) {
      var pf = fc.getAttribute('data-asr-finish');
      var was = state.finishPick === pf;
      state.finishPick = was ? null : pf;
      /* optimistic: reflect the selection instantly, before the fetch returns */
      Array.prototype.forEach.call(fc.parentNode.querySelectorAll('.asr-fam-chip'), function (c) {
        c.classList.toggle('on', !was && c === fc);
      });
      run(true);
      return;
    }
    if (e.target.closest('[data-asr-finish-all]')) {
      state.finishPick = null;
      run(true);
      return;
    }
    /* grid/list view toggle */
    var vb = e.target.closest('.ac-density button[data-view]');
    if (vb) {
      var view = vb.getAttribute('data-view');
      applyView(view);
      try { localStorage.setItem('ac-view', view); } catch (_) {}
      return;
    }
    /* accordion toggle */
    var q = e.target.closest('.fg-q');
    if (q && railBox.contains(q)) {
      var g = q.closest('.fgroup');
      var key = g && g.getAttribute('data-asr-group');
      var open = g.classList.toggle('on');
      q.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (key) state.open[key] = open;
      return;
    }
    /* show-all toggle */
    var mt = e.target.closest('[data-asr-more-toggle]');
    if (mt) {
      var k = mt.getAttribute('data-asr-more-toggle');
      state.showAll[k] = !state.showAll[k];
      var grp = mt.closest('.fgroup');
      if (grp) grp.classList.toggle('show-all', state.showAll[k]);
      mt.textContent = state.showAll[k] ? 'Show less' : 'Show all (' + grp.querySelectorAll('.fopt,.fbr,.fsw,.fsh').length + ')';
      return;
    }
    /* sort dropdown open/close */
    if (e.target.closest('[data-asr-sort-btn]')) {
      e.stopPropagation();
      var op = sortWrap.classList.toggle('ac-open');
      sortBtn.setAttribute('aria-expanded', op ? 'true' : 'false');
      return;
    }
    var opt = e.target.closest('.ac-adrop-opt');
    if (opt) {
      var val = opt.getAttribute('data-val');
      sortWrap.querySelectorAll('.ac-adrop-opt').forEach(function (o) { o.classList.remove('ac-on'); });
      opt.classList.add('ac-on');
      if (sortLbl) sortLbl.textContent = opt.querySelector('.ac-lbl').textContent;
      sortWrap.classList.remove('ac-open');
      sortBtn.setAttribute('aria-expanded', 'false');
      if (val !== state.sort) { state.sort = val; run(true); }
      return;
    }
    /* pill remove */
    var p = e.target.closest('[data-asr-pill-type]');
    if (p) {
      e.preventDefault();
      var pt = p.getAttribute('data-asr-pill-type');
      if (pt === 'price') { state.f.priceMin = null; state.f.priceMax = null; }
      else { state.f[pt].delete(p.getAttribute('data-asr-pill-val')); }
      run(true);
      return;
    }
    /* clear all */
    if (e.target.closest('[data-asr-clear]')) {
      e.preventDefault();
      state.f.vendor.clear();
      MFACETS.forEach(function (g) { state.f[g.key].clear(); });
      state.f.priceMin = null; state.f.priceMax = null;
      run(true);
      return;
    }
    /* mobile drawer */
    if (e.target.closest('[data-asr-filter-open]')) { openDrawer(); return; }
    if (e.target.closest('[data-asr-filter-close]')) { closeDrawer(); return; }
    /* retry */
    if (e.target.closest('[data-asr-retry]')) { e.preventDefault(); run(false); return; }
  });

  /* manual click = user takeover: stop auto-loading (but the button keeps working). */
  if (moreBtn) moreBtn.addEventListener('click', function () { autoLoads = AUTO_MAX; loadMore(); });

  /* capped infinite scroll: auto-fire loadMore for the first AUTO_MAX batches as the
     load-more zone nears the viewport, then hand back to the manual button so the
     footer stays reachable. moreWrap is persistent, so one observer suffices. pumpAuto
     re-checks geometry each call (not a cached flag) so a post-load chain only
     continues while the zone is genuinely still near - short pages fill, tall don't. */
  var LEAD = 1400;   /* how far ahead (px) we start the fetch; must match rootMargin below */
  function nearZone() {
    if (!moreWrap || moreWrap.style.display === 'none') return false;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return moreWrap.getBoundingClientRect().top <= vh + LEAD;
  }
  function pumpAuto() {
    if (autoLoads >= AUTO_MAX || state.loading) return;
    if (state.page + 1 >= state.nbPages) return;   /* no more results */
    if (!nearZone()) return;
    autoLoads++;
    loadMore();
  }
  if (moreWrap && 'IntersectionObserver' in window) {
    new IntersectionObserver(pumpAuto, { rootMargin: LEAD + 'px 0px' }).observe(moreWrap);
  }
  if (scrimEl) scrimEl.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeDrawer(); });
  document.addEventListener('click', function (e) {
    if (sortWrap && !sortWrap.contains(e.target)) {
      sortWrap.classList.remove('ac-open');
      if (sortBtn) sortBtn.setAttribute('aria-expanded', 'false');
    }
  });

  if (formEl) formEl.addEventListener('submit', function (e) {
    e.preventDefault();
    var qv = (inputEl.value || '').trim();
    var nq = (window.BSDSearch && BSDSearch.normalize) ? (BSDSearch.normalize(qv) || qv) : qv;
    try { history.replaceState(null, '', '/search?q=' + encodeURIComponent(nq)); } catch (_) {}
    state.q = qv;
    run(true);
  });

  /* ---- boot ---- */
  try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (_) {}
  parseUrlIntoState();
  if (inputEl) inputEl.value = state.q;
  syncSortUI();
  var savedView = null;
  try { savedView = localStorage.getItem('ac-view'); } catch (_) {}
  /* instant restore on back; otherwise a normal search */
  if (!tryRestore()) {
    applyView(savedView === 'list' ? 'list' : 'grid');
    run(true);
  }
  window.addEventListener('pagehide', function () { takeSnapshot(); stampUrl(); });
  /* card-click = the PRIMARY snapshot moment (synchronous, before navigation -
     iOS Safari can drop pagehide-time storage writes AND storage/history.state
     entirely on back, hence the URL stamp). Capture phase so nothing upstream
     can swallow it. */
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href*="/products/"]');
    if (!a || !root.contains(a)) return;
    dbg('OUT ' + location.origin + ' click -> ' + a.href + ' scrollY=' + Math.round(window.scrollY) + ' pages=' + ((state.page || 0) + 1));
    takeSnapshot();  /* BEFORE stampUrl - the key must be token-free */
    stampUrl();
    stSet('asr_out', String(Date.now()));
  }, true);
  /* BFCACHE RESUME = the path Safari actually takes when its cache holds (the
     round-5 forensics: back fired pageshow persisted=true and NO reload - the
     page came back alive with DOM+JS intact but AT THE TOP, because our own
     history.scrollRestoration='manual' also tells Safari not to restore scroll
     on resume). So: re-apply the click-time scroll ourselves. READ-ONLY on the
     restore signals - never clear the marker/snapshot here (an earlier cleanup
     here deleted the marker mid-back on the show-then-discard-then-reload
     variant of iOS swipe-back; the reload ladder still needs them). */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    dbg('pageshow persisted=true');
    if ((window.scrollY || 0) > 40) { dbg('resume: browser kept scroll (' + Math.round(window.scrollY) + ')'); return; }
    var snap = null;
    try { snap = JSON.parse(stGet('asr_snap') || 'null'); } catch (e2) {}
    if (!snap || snap.key !== keyOf()) { dbg('resume: no matching snapshot'); return; }
    var y = snap.scrollY || 0;
    if (!y) return;
    var de = document.documentElement, prevSB = de.style.scrollBehavior;
    de.style.scrollBehavior = 'auto';
    requestAnimationFrame(function () {
      window.scrollTo(0, y);
      requestAnimationFrame(function () {
        window.scrollTo(0, y);
        de.style.scrollBehavior = prevSB;
        dbg('resume: scrolled to ' + y + ' actual=' + Math.round(window.scrollY));
      });
    });
    /* Safari occasionally needs a beat after resume before layout accepts the jump */
    setTimeout(function () {
      if (Math.abs((window.scrollY || 0) - y) > 40) {
        window.scrollTo(0, y);
        dbg('resume: late re-jump, actual=' + Math.round(window.scrollY));
      }
    }, 250);
  });
})();
